if (window.relewiseAPI?.enable_relewise) {

  // ─── Config ────────────────────────────────────────────────────────────────

  const rlwPrefix = window.relewiseAPI.relewise_prefix_data;

  const PRODUCT_DATA_KEYS = [
    'IsAvailable',
    'Vendor',
    `${rlwPrefix}_ShopifyHandle`,
    `${rlwPrefix}_ImageUrls`,
  ];

  // AX's "has a member price" variant flag — the "Medlemspris" facet's key, and
  // the filter member price collections apply (rlwState.memberVariantsOnly).
  const RLW_MEMBER_FLAG_KEY = `${rlwPrefix}_ilva.has_member_price`;

  /**
   * Restrict a request to products belonging to THIS Shopify store.
   *
   * The Relewise dataset is shared by several ILVA stores (ilvadk, ilvase and
   * the test stores), which Relewise confirms is a supported setup. Every
   * product and variant carries a `ShopifyDomains` list naming the stores it
   * belongs to, and filtering on it is the vendor's prescribed pattern — it is
   * what their own UI components do natively on every request. Without it a
   * DK request also receives test-store rows, which have no ilvadk page to link
   * to and no ilvadk variant id to add to the cart.
   *
   * Both filters are required. The product filter alone still lets a foreign
   * VARIANT through on a product that legitimately belongs to this store, which
   * surfaces as another store's price or a missing variant id on an otherwise
   * valid card — measured on the collection grid as 5 foreign variants and 3
   * missing variant ids per 50 rendered cards.
   *
   * Filter in the request, never after the response: dropping rows client-side
   * eats into the page size and can empty a carousel that Relewise would
   * otherwise have filled.
   *
   * Product categories and content/pages get their own scoped filters below
   * (rlwCategoryStoreScopeFilter, rlwContentStoreScopeFilter) — both use
   * `filterOutIfKeyIsNotFound: false` so an entity that turns out not to carry
   * the key at all is passed through unfiltered rather than dropped, since the
   * key's presence on every Category/Content entity has only been sampled for
   * ilvadk, not ilvase.
   *
   * @param {object} f - Relewise filter builder, from `.filters(f => ...)`.
   * @returns {object} The same builder, for chaining.
   */
  function rlwStoreScopeFilter(f) {
    return f
      .addProductDataFilter('ShopifyDomains', c => c.addContainsCondition(rlwPrefix))
      .addVariantDataFilter('ShopifyDomains', c => c.addContainsCondition(rlwPrefix));
  }

  // Categories carry a singular `ShopifyDomain` key (confirmed via a 52-item
  // sample, all ilvadk) rather than the plural `ShopifyDomains` list used on
  // products/variants — hence Equals instead of Contains. filterOutIfKeyIsNotFound
  // is explicitly false: if the key is ever absent, pass the category through
  // rather than blanking the "Categories" section of the search overlay.
  function rlwCategoryStoreScopeFilter(f) {
    return f.addProductCategoryDataFilter(
      'ShopifyDomain',
      c => c.addEqualsCondition(rlwPrefix),
      undefined,
      false
    );
  }

  // Content (Page/Article/Blog) carries the same singular `ShopifyDomain` key
  // as categories — confirmed against the live index (all 303 entities, both
  // stores). This used to filter on the plural `ShopifyDomains` with Contains,
  // which no content entity has, so filterOutIfKeyIsNotFound: false let every
  // row through and the DK overlay listed SE articles/bolighuse too. Kept
  // permissive for the same reason as the category filter.
  function rlwContentStoreScopeFilter(f) {
    return f.addContentDataFilter(
      'ShopifyDomain',
      c => c.addEqualsCondition(rlwPrefix),
      undefined,
      false
    );
  }

  const rlwState = {
    collectionId: null,
    collectionHandle: null,
    // Set instead of collectionId on the search results page — rlwFetchListing
    // branches on whichever of the two is present (see rlwInitListing).
    searchTerm: null,
    // Collection grid only: narrow each product to its member-price variant
    // (data-member-variants, ClickUp 869fgh6tu).
    memberVariantsOnly: false,
    rootUrl: '/',
    perPage: 16,
    currentPage: 1,
    currentSorting: '',
    activeFacets: {},
    lastFacets: [],
    // Total hits of the last rendered response; null until the first one.
    lastHits: null,
    searchConfig: null,
    visibleFacetPills: 7,
    moreFiltersExpanded: false,
  };

  let _rlwOpenFacetKey = null;
  // Refs for the open dropdown so it can be re-positioned when the filter row
  // scrolls under it (mobile scrolls that row — see relewise.css).
  let _rlwOpenDropdown = null;
  let _rlwFacetConfigCache = null;
  let _rlwFetchSeq = 0;
  // Native-card fetching (collection grid): aborts in-flight card fetches when
  // a newer render starts, stashes a skeleton <li> for reload placeholders and
  // caches fetched card HTML for the session (filter/sort/page changes keep
  // re-showing the same products).
  let _rlwCardAbort = null;
  let _rlwSkeletonLi = null;
  // The listing wrapper rlwInitListing last ran on. The theme editor re-runs
  // init on a re-rendered section's fresh wrapper, so page-level listeners
  // follow this rather than closing over one wrapper each.
  let _rlwListingWrapper = null;
  let _rlwResizeBound = false;
  const _rlwCardHtmlCache = new Map();
  const RLW_CARD_CACHE_MAX = 300;

  // ─── Facet config ──────────────────────────────────────────────────────────
  // Facets are driven entirely by the Relewise app embed's "Collection pages"
  // Search Configuration (My Relewise dashboard), read via the app's own
  // window.ShopifyRelewise global — not by a theme setting. We read `.config`
  // directly (rather than calling the app's `.apply(builder)` helper) because
  // we render our own pill/dropdown UI instead of the app's web components.
  // requiresShopDomain mirrors the same per-shop key prefix already used for
  // ShopifyHandle/ImageUrls etc. above (Relewise namespaces custom data keys
  // per shop in multi-store datasets).

  const RLW_FACET_TYPE_MAP = {
    SalesPriceRangeFacet: 'price',
    DataBooleanValueFacet: 'boolean',
    DataStringValueFacet: 'string',
    // Both map to 'number': the TS SDK has no separate request builder for
    // Integer-typed data facets (addProductDataDoubleValueFacet is the only
    // discrete-value-list method for numeric fields, confirmed against the
    // full FacetBuilder API), so Integer- and Double-typed dashboard facets
    // are requested identically.
    DataIntegerValueFacet: 'number',
    DataDoubleValueFacet: 'number',
    // Min/max slider over a numeric data field (dimensions, compare-at
    // price…). Same {min, max} state, URL format and slider UI as 'price', but
    // requested via addProductDataDoubleRangeFacet against the data key.
    DataDoubleRangeFacet: 'range',
  };

  // Range facets have no unit in the dashboard config. Price-like keys render
  // as money; Perfion dimension fields are all centimetres ("Længde (CM)").
  const RLW_RANGE_CM_KEY = /^perfion\.(laengde|bredde|dybde|bredde_dybde|hoejde|diameter|siddehoejde|siddedybde)$/;
  function rlwRangeFormatter(dashboardKey) {
    if (/price/i.test(dashboardKey)) return rlwMoney;
    const unit = RLW_RANGE_CM_KEY.test(dashboardKey) ? ' cm' : '';
    return n => `${Number(n).toLocaleString(document.documentElement.lang || 'da', { maximumFractionDigits: 1 })}${unit}`;
  }

  // `keys` are tried in order against window.ShopifyRelewise.searchConfigurations
  // — the first one with a config wins. The search results page passes
  // ['search', 'collection']: 'search' is this integration's best guess at
  // the key the Relewise app assigns a merchant-configured "Search results
  // page" Search Configuration (unconfirmed — the app's embed script sets
  // this global independently of the theme, and there is no documented list
  // of its keys). Falling back to 'collection' means search facets degrade
  // to the same Color/Size/Material/Price facets used on collection pages
  // rather than disappearing outright if 'search' doesn't exist. Collection
  // pages pass only ['collection'], unchanged from before this was
  // parameterised.
  function rlwWaitForSearchConfiguration(maxWaitMs, intervalMs, keys = ['collection']) {
    const start = Date.now();
    return new Promise(resolve => {
      (function poll() {
        const configs = window.ShopifyRelewise?.searchConfigurations;
        const config = keys.map(key => configs?.[key]?.config).find(Boolean);
        if (config || Date.now() - start >= maxWaitMs) {
          if (!config) console.warn(`[Relewise] No search configuration found for key(s) "${keys.join('", "')}" on window.ShopifyRelewise — facets will be unavailable.`);
          resolve(config || null);
        } else {
          setTimeout(poll, intervalMs);
        }
      })();
    });
  }

  // URL-safe slug of a facet heading: "Længde" → "laengde", "Ben/stel farve"
  // → "ben-stel-farve". æ/ø/œ/ß are spelled out by hand because NFD only
  // decomposes letters with a combining mark (å → a, é → e).
  const RLW_SLUG_LIGATURES = { 'æ': 'ae', 'ø': 'oe', 'œ': 'oe', 'ß': 'ss', 'þ': 'th', 'ð': 'd', 'ł': 'l' };
  function rlwSlugify(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[æøœßþðł]/g, c => RLW_SLUG_LIGATURES[c])
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');
  }

  function rlwGetFacetConfigs() {
    if (_rlwFacetConfigCache) return _rlwFacetConfigCache;

    const locale = document.documentElement.lang || '';
    _rlwFacetConfigCache = (rlwState.searchConfig?.facets || [])
      .filter(f => f.enabled)
      .map(f => {
        const kind = RLW_FACET_TYPE_MAP[f.type];
        if (!kind) {
          console.warn(`[Relewise] Unsupported facet type "${f.type}" for key "${f.key}" — skipping.`);
          return null;
        }
        // A price-range facet has no key on the wire — addSalesPriceRangeFacet()
        // takes no key argument — so the dashboard's `key` ("Price") is only an
        // organizational label there. Relewise always identifies it in the
        // response as the fixed system field "SalesPrice"; match on that
        // instead, regardless of what the merchant typed as the facet's key.
        return {
          key: kind === 'price' ? 'SalesPrice' : (f.requiresShopDomain ? `${rlwPrefix}_${f.key}` : f.key),
          // Previous URL name (the raw dashboard key) — still accepted by
          // rlwReadUrlState so links shared before the slug names keep working.
          legacyUrlKey: kind === 'price' ? 'price' : f.key,
          label: (locale && f.headingsByLocale?.[locale]) || f.defaultHeading || f.key,
          kind,
          selectionStrategy: f.selectionStrategy || 'Product',
          // Slider value/chip formatter — price and range kinds only.
          format: kind === 'price' ? rlwMoney : (kind === 'range' ? rlwRangeFormatter(f.key) : null),
        };
      })
      .filter(Boolean)
      // On a member price collection every card is already a member-price
      // variant, so the "Medlemspris" facet could change nothing: no pill, no
      // chip, no URL name. rlwFetchListing sends it as a hidden selection
      // instead (ClickUp 869fgh6tu).
      .filter(cfg => !(rlwState.memberVariantsOnly && cfg.key === RLW_MEMBER_FLAG_KEY));

    // Name used in the listing URL (rlwWriteUrlState / rlwReadUrlState): a
    // slug of the heading the shopper sees, so internal metafield keys like
    // "perfion.laengde" never show up. Facets without a heading fall back to
    // the last segment of their key. Clashing slugs get -2, -3… in config order.
    const usedUrlKeys = new Set();
    _rlwFacetConfigCache.forEach(cfg => {
      const base = (cfg.label !== cfg.legacyUrlKey && rlwSlugify(cfg.label))
        || rlwSlugify(String(cfg.legacyUrlKey).split('.').pop())
        || 'filter';
      let urlKey = base;
      for (let n = 2; usedUrlKeys.has(urlKey); n++) urlKey = `${base}-${n}`;
      usedUrlKeys.add(urlKey);
      cfg.urlKey = urlKey;
    });
    // Don't read a legacy name that is now another facet's slug name.
    _rlwFacetConfigCache.forEach(cfg => {
      if (cfg.legacyUrlKey !== cfg.urlKey && usedUrlKeys.has(cfg.legacyUrlKey)) cfg.legacyUrlKey = null;
    });

    return _rlwFacetConfigCache;
  }

  /* GA4 dataLayer (s360 spec, ClickUp 869ejnku8): filter_click for the
     Relewise-rendered PLP facets - mirrors the theme-facets hook in
     datalayer.js. Called AFTER the state mutation so filter_selection
     reflects the new selection; no-ops when tracking is disabled. */
  function rlwTrackFacetChange(cfg, changedValue) {
    if (!window.ilvaTrack) return;
    const globals = (window.dataLayer || []).find(e => e && typeof e.loginStatus === 'string') || {};
    const active = rlwState.activeFacets[cfg.key];
    let selection = '';
    if (Array.isArray(active)) selection = active.map(String).join(',');
    else if (active && typeof active === 'object') selection = `${active.min ?? ''}-${active.max ?? ''}`;
    else if (active !== undefined) selection = rlwBooleanLabel(active);
    window.ilvaTrack('filter_click', {
      filter: cfg.label,
      filter_selection: selection,
      filter_type: cfg.label,
      filter_value: changedValue,
      page_type: globals.pageType || ''
    });
  }

  function rlwCountActiveFacets(facetKey) {
    const active = rlwState.activeFacets[facetKey];
    // Checked ahead of `!active` — an explicit `false` selection (e.g. "No")
    // is a real active filter, not an absent one.
    if (typeof active === 'boolean') return 1;
    if (!active) return 0;
    if (Array.isArray(active)) return active.length;
    if (active.min != null || active.max != null) return 1;
    return 0;
  }

  // ─── User helpers ──────────────────────────────────────────────────────────

  // The app embed sets window.ShopifyRelewise from its own DOMContentLoaded
  // handler. Today that runs before ours, but Relewise documents no ready
  // event, so wait for it the way rlwInitListing waits for the search
  // configuration on the same global. Shared by every caller, so a missing
  // app costs a single wait per page rather than one per request.
  let _rlwAppUserApiReady = null;
  function rlwWaitForAppUserApi(maxWaitMs = 5000, intervalMs = 100) {
    if (!_rlwAppUserApiReady) {
      const start = Date.now();
      _rlwAppUserApiReady = new Promise(resolve => {
        (function poll() {
          if (typeof window.ShopifyRelewise?.getUserAsync === 'function') {
            resolve(true);
          } else if (Date.now() - start >= maxWaitMs) {
            console.warn('[Relewise] window.ShopifyRelewise.getUserAsync unavailable — nothing will be sent to Relewise.');
            resolve(false);
          } else {
            setTimeout(poll, intervalMs);
          }
        })();
      });
    }
    return _rlwAppUserApiReady;
  }

  /**
   * The Relewise user for every theme request, taken from the Relewise app
   * embed (window.ShopifyRelewise.getUserAsync) so theme and app requests
   * share one identity instead of each minting their own temporary user.
   *
   * The app decides consent and identity: with marketing consent the user has
   * a temporaryId (Shopify's consent id) plus, when logged in, the customer's
   * authenticatedId — sending both together is what links pre-login browsing
   * to the customer in Relewise. Without consent it is anonymous, even when
   * logged in. It also sets the Country classification (Shopify.country).
   *
   * The theme only adds the Rubiq identifier and club-member flag, and only
   * for a user the app already identified as the customer.
   *
   * Resolves to null when the app's user is unavailable (app embed off, or
   * not initialised within rlwWaitForAppUserApi's wait); callers then send
   * nothing to Relewise.
   */
  async function rlwGetUser() {
    if (!(await rlwWaitForAppUserApi())) return null;

    let appUser;
    try {
      appUser = await window.ShopifyRelewise.getUserAsync();
    } catch (e) {
      console.error('[Relewise] Could not get user from the Relewise app', e);
      return null;
    }
    if (!appUser) return null;

    // Copy: the app resolves the same object for every caller, its own
    // requests included, so it must never be mutated.
    const user = { ...appUser };
    if (user.authenticatedId) {
      const rubiqId = window.relewiseAPI.customer_rubiq_id;
      if (rubiqId) user.identifiers = { ...(user.identifiers || {}), rubiqid: rubiqId };
      user.data = {
        ...(user.data || {}),
        isClubMember: Relewise.DataValueFactory.boolean(!!window.relewiseAPI.customer_is_club_member),
      };
    }
    return user;
  }

  // For callers that need a user: rejects instead of sending a request
  // without one (see rlwGetUser).
  async function rlwRequireUser() {
    const user = await rlwGetUser();
    if (!user) throw new Error('Relewise app user unavailable');
    return user;
  }

  function rlwGetTracker() {
    if (!window.Relewise?.Tracker) return null;
    return new Relewise.Tracker(
      window.relewiseAPI.relewise_dataid,
      window.relewiseAPI.relewise_api,
      { serverUrl: window.relewiseAPI.relewise_server_url }
    );
  }

  const RLW_WISHLIST_PRODUCT_INFO_QUERY = `
    query WishlistProductInfo($handle: String!, $variantGid: ID!, $hasVariant: Boolean!) {
      product(handle: $handle) {
        varenummer: metafield(namespace: "perfion", key: "varenummer") { value }
      }
      variant: node(id: $variantGid) @include(if: $hasVariant) {
        ... on ProductVariant { sku }
      }
    }
  `;

  // Relewise's catalog is keyed by the perfion.varenummer product metafield
  // (not Shopify's native product id) and, for variants, by the full SKU
  // string (not Shopify's native variant id either). SKUs sometimes carry the
  // varenummer as a "<varenummer>-<variantId>" prefix, but for now Relewise
  // wants the whole SKU as-is — this may change to just the suffix later.

  // The wishlist events only give us a handle + Shopify's native variant
  // id, so every call needs a Storefront API lookup first to translate them
  // into Relewise's identifiers above.
  async function rlwTrackProductFavorite(handle, variantId, isFavorite) {
    if (!window.relewiseAPI.customer_id || !handle) return;
    const tracker = rlwGetTracker();
    if (!tracker) return;

    const shopDomain = window.relewiseAPI.relewise_prefix_data;
    const storefrontToken = window.relewiseAPI.storefront_api_token;
    if (!shopDomain || !storefrontToken) return;

    // Favourites are only tracked for an identified customer, and the app only
    // identifies one with marketing consent (see rlwGetUser).
    const user = await rlwGetUser();
    if (!user?.authenticatedId) return;

    const hasVariant = Boolean(variantId);

    return fetch(`https://${shopDomain}/api/2026-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query: RLW_WISHLIST_PRODUCT_INFO_QUERY,
        variables: {
          handle,
          hasVariant,
          variantGid: hasVariant ? `gid://shopify/ProductVariant/${variantId}` : '',
        },
      }),
    })
      .then(res => (res.ok ? res.json() : null))
      .then(result => {
        const varenummer = result?.data?.product?.varenummer?.value;
        if (!varenummer) return;

        const product = { productId: String(varenummer) };
        const sku = result?.data?.variant?.sku;
        if (hasVariant && sku) product.variantId = sku;

        return tracker.trackProductEngagement({
          user,
          product,
          engagement: { isFavorite },
        });
      })
      .catch(err => console.error('[Relewise wishlist]', err));
  }

  // The theme-native wishlist store (assets/wishlist.js) fires these on every
  // heart click.
  function rlwInitWishlistEngagementListeners() {
    document.addEventListener('DTAILS_WISHLIST:wishlist:add', e => {
      const { productHandle, variantId } = e.detail || {};
      rlwTrackProductFavorite(productHandle, variantId, true);
    });
    document.addEventListener('DTAILS_WISHLIST:wishlist:remove', e => {
      const { productHandle, variantId } = e.detail || {};
      rlwTrackProductFavorite(productHandle, variantId, false);
    });
  }

  // ─── Formatting ───────────────────────────────────────────────────────────

  function rlwPrice(raw) {
    if (raw == null) return null;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'object') return raw.parsedValue ?? null;
    return null;
  }

  // ─── Indexed metafield readers ────────────────────────────────────────────
  // Relewise wraps each data key as { type, value, isCollection }, and the inner
  // value arrives in one of three shapes depending on how the field was mapped:
  // a scalar, a typed collection ({ $values: [...] }), or a JSON string. These
  // three helpers normalise all of them so callers never branch on wire format.

  function rlwDataValue(data, key) {
    const raw = data?.[key]?.value;
    if (raw == null) return null;
    if (typeof raw === 'object' && raw.$values !== undefined) return raw.$values;
    return raw;
  }

  /** Reads a json-typed metafield, parsing it when it arrives as a string. */
  function rlwDataJson(data, key) {
    const v = rlwDataValue(data, key);
    if (v == null || typeof v !== 'string') return v;
    try {
      return JSON.parse(v);
    } catch {
      return null;
    }
  }

  /** Reads a list-of-references metafield as a flat array of id strings. */
  function rlwDataList(data, key) {
    const v = rlwDataValue(data, key);
    if (v == null) return [];
    if (Array.isArray(v)) return v;
    if (typeof v !== 'string') return [];
    const s = v.trim();
    if (!s) return [];
    // A single reference is delivered bare, a list as a JSON array.
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [s];
  }

  /**
   * The Relewise data key for the NYHED ("New") boolean metafield. Built from
   * the merchant setting rather than hardcoded so renaming the key in the theme
   * editor keeps this path working too. Parsed exactly as product-label.liquid
   * parses the same setting, or the two paths diverge on identical input: a
   * `product.metafields.` / `variant.metafields.` prefix is stripped (several
   * snippets accept that qualified form — _faq-metafield-content.liquid
   * documents it), and a blank setting turns the source OFF rather than falling
   * back to a compiled-in default, which is what the Liquid's
   * `settings.new_label_metafield != blank` gate does. Returns '' when there is
   * no usable key. ClickUp 869f3974k.
   */
  function rlwNyhedDataKey() {
    const key = String(window.relewiseAPI?.new_label_metafield || '')
      .trim()
      .replaceAll('product.metafields.', '')
      .replaceAll('variant.metafields.', '');
    // Both halves must be present. "nyhed" (no namespace) and "perfion." (no
    // key) each leave one of nyhed_ns / nyhed_key blank in the Liquid, which
    // makes that branch inert — same outcome here.
    const [ns, mfKey] = key.split('.');
    if (!ns || !mfKey) return '';
    return `${rlwPrefix}_${key}`;
  }

  /**
   * NYHED badge gate. The Perfion definition is variant-scoped like
   * perfion.fast_lav_pris: the variant value wins, the product level is only a
   * fallback, and an explicit variant-level false beats a product-level true —
   * the precedence product-label.liquid applies. Relewise returns booleans as
   * either true or "true" depending on how the field was mapped, so accept
   * both. No zero-price guard: "New" is not a price claim, unlike FAST LAV PRIS.
   * Gated on the same two merchant settings as the Liquid badge, so switching
   * either off clears it from search too.
   */
  function rlwShowNyhed(product) {
    const cfg = window.relewiseAPI || {};
    if (cfg.show_new_label === false || cfg.show_new_label_metafield === false) return false;
    const key = rlwNyhedDataKey();
    if (!key) return false;
    let raw = product.variant?.data?.[key]?.value;
    if (raw === undefined || raw === null) raw = product.data?.[key]?.value;
    return raw === true || raw === 'true';
  }

  function rlwEscape(s) {
    if (s == null) return '';
    const d = document.createElement('div');
    d.textContent = String(s);
    return d.innerHTML.replace(/"/g, '&quot;');
  }

  // Card images arrive from the catalog feed as a single full-resolution URL, so
  // a card that renders one had no way to ask for the size it actually needs:
  // the browser got one fixed source for every breakpoint and pixel density.
  // Shopify CDN URLs take `width`/`height`/`crop` params, so those become a real
  // srcset below. A URL on any other host (a PIM's own image server) carries no
  // such contract and is handed back exactly as the feed supplied it.

  // Mirrors the ladder in snippets/product-card.liquid so JS cards and Liquid
  // cards request the same sizes.
  const RLW_CARD_WIDTHS = [320, 460, 600, 700, 800, 900, 1200];
  // The 2/3/4-column edge-to-edge grid in relewise.css, whose gap is 0.4rem (4px
  // at this theme's 10px rem base). Change the grid there and change this too.
  const RLW_CARD_SIZES = '(min-width: 1024px) calc((100vw - 12px) / 4), (min-width: 769px) calc((100vw - 8px) / 3), calc((100vw - 4px) / 2)';

  /**
   * Parsed URL when the CDN can resize it, else null.
   * @param {string} url - Feed image URL, absolute or protocol-relative.
   * @returns {URL|null}
   */
  function rlwResizableUrl(url) {
    if (!url) return null;
    // Require an absolute (or protocol-relative) URL, or an explicit CDN path.
    // Anything else — a relative value, a malformed one — would resolve against
    // the storefront's own origin, which is itself a .myshopify.com host, and be
    // mistaken for a resizable CDN image: we would then emit a srcset of URLs
    // that 404 instead of leaving the feed's own src alone.
    if (!/^(https?:)?\/\//i.test(url) && !url.startsWith('/cdn/shop/')) return null;
    let u;
    try {
      u = new URL(url, window.location.href);
    } catch (e) {
      return null;
    }
    const resizable = u.hostname === 'cdn.shopify.com'
      || u.hostname.endsWith('.myshopify.com')
      || u.pathname.includes('/cdn/shop/');
    return resizable ? u : null;
  }

  /**
   * The feed URL asked for at one size, square-cropped to match the card's 1:1
   * media box. Without the crop a letterboxed source under-delivers: `width=600`
   * on a 2000x904 image returns 600x271, and object-fit: cover then scales those
   * 271 rows up to fill 600 — the graininess this pairs with the srcset to fix.
   * Returns the URL untouched when it is not resizable.
   * @param {string} url - Feed image URL.
   * @param {number} width - Requested width in CSS pixels.
   * @returns {string}
   */
  function rlwSquareImageAt(url, width) {
    const u = rlwResizableUrl(url);
    if (!u) return url;
    u.searchParams.set('width', String(width));
    u.searchParams.set('height', String(width));
    u.searchParams.set('crop', 'center');
    return u.href;
  }

  /**
   * A `srcset`/`sizes` attribute pair for a square card image, or '' when the URL
   * cannot be resized — in which case the caller's plain `src` stays the only
   * source, exactly as before.
   * @param {string} url - Feed image URL.
   * @param {number[]} widths - Candidate widths in ascending order.
   * @param {string} sizes - `sizes` attribute value.
   * @returns {string} Attributes with a leading space, or ''.
   */
  function rlwSquareImageSrcset(url, widths, sizes) {
    if (!rlwResizableUrl(url)) return '';
    const set = widths.map((w) => `${rlwSquareImageAt(url, w)} ${w}w`).join(', ');
    return ` srcset="${rlwEscape(set)}" sizes="${rlwEscape(sizes)}"`;
  }

  // Reuse the global money formatting functions from main.js (which loads first).
  // If for any reason they're not available (e.g. main.js didn't load), fall back
  // to a minimal implementation.
  function rlwFormatMoney(cents, format) {
    if (window.applyMoneyFormat) {
      return window.applyMoneyFormat(cents, format);
    }
    // Fallback. Substitutes ANY placeholder, not just `{{amount}}` — a format the
    // fallback does not recognise must still come out as a number with the wrong
    // separator, never as literal `{{…}}` on a customer-facing card.
    const num = cents / 100;
    const [int, frac] = num.toFixed(2).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      ? format.replace(/\{\{\s*\w+\s*\}\}/g, `${grouped}.${frac}`)
      : `${grouped}.${frac}`;
  }

  function rlwMoney(amount) {
    if (window.formatMoney) {
      return window.formatMoney(amount);
    }
    // Fallback
    let out;
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      out = Shopify.formatMoney(Math.round(amount * 100));
    } else {
      const format = window.theme?.settings?.moneyFormat || window.theme?.settings?.moneyWithCurrencyFormat;
      out = format ? rlwFormatMoney(Math.round(amount * 100), format) : amount.toFixed(2);
    }
    return out.replace(/([.,])00(?=\D*$)/, '');
  }

  // ─── API ──────────────────────────────────────────────────────────────────

  // Drives both the collection grid (rlwState.collectionId set) and the
  // search results grid (rlwState.searchTerm set) — see rlwInitListing.
  // Facets/sorting/pagination are identical either way; the only branch is
  // the ImmediateParent category filter vs. a term search.
  async function rlwFetchListing(collectionId, term, perPage, page, sorting, facets) {
    const settings = {
      language: window.relewiseAPI.language || document.documentElement.lang || 'da',
      currency: (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'DKK',
      displayedAtLocation: term ? 'Search results page' : 'Category page',
      user: await rlwRequireUser(),
    };

    const configs = rlwGetFacetConfigs();
    // Client-side cards read their data straight off this response, so they
    // add their keys to it (rlwCardDataKeys) — collection mode only, and only
    // when the feature is on, so the search grid's payload is unchanged.
    const cardKeys = !term && rlwClientCardsEnabled() ? rlwCardDataKeys() : { product: [], variant: [] };

    let productBuilder = new Relewise.ProductSearchBuilder(settings)
      .setSelectedProductProperties({
        displayName: true,
        dataKeys: [
          ...PRODUCT_DATA_KEYS,
          ...cardKeys.product,
          // NYHED. Variant-scoped in Perfion, so the variant list below is the
          // one that matters — this product-level request exists purely so the
          // variant-then-product fallback works if the feed ever adds it, same
          // as in rlwSearchDropdown. Empty when the merchant cleared the setting,
          // hence the filter. ClickUp 869f3974k.
          rlwNyhedDataKey(),
        ].filter(Boolean),
        pricing: true,
        brand: true,
      })
      .setSelectedVariantProperties({
        // Built-in variant prices — the card variant's own catalogue price,
        // not the product-level aggregate (see rlwCardModel).
        pricing: cardKeys.variant.length > 0,
        dataKeys: [
          `${rlwPrefix}_ShopifyVariantId`,
          // NYHED — the "New" badge's boolean metafield, variant-scoped in
          // Perfion. Inert until the Relewise feed indexes it, the same
          // enablement perfion.fast_lav_pris already has. Empty when the
          // merchant cleared the setting, hence the filter. ClickUp 869f3974k.
          rlwNyhedDataKey(),
          ...cardKeys.variant,
        ].filter(Boolean),
      });

    // Collection mode: one variant per product, Relewise's own "most
    // popular" pick (ExplodedVariants). Search mode skips this in favour of
    // the variantRequestSettings override below, which asks for the variant
    // most relevant to the search TERM instead — same reasoning as the
    // predictive-search dropdown (rlwSearchDropdown), which the "Relewise
    // search should pull variants" fix already applied there.
    if (!term) {
      productBuilder = productBuilder.setExplodedVariants(1);
    }

    productBuilder = productBuilder
      .filters(f => {
        // The category filter covers product-level collection membership. Shopify
        // collections whose rules target variants (e.g. variant metafield
        // perfion.farvefilter = Blå) store their membership on the matching
        // variants instead, as `<shop>_ShopifyCollectionIds` — without this
        // filter every product with ONE matching variant comes back with its
        // most popular variant, not the matching one. Per Relewise's Shopify
        // custom-collection-page docs, which pair the two filters. The value is
        // an explicitly typed String DataValue: the SDK bundled by the app embed
        // would otherwise send a bare, numeric-looking string for a StringList key.
        //
        // A variant filter never removes a product, only variants: a product
        // whose matching variants are all excluded elsewhere (e.g. the
        // centrallager_in_stock merchandising rule) still comes back, with no
        // variant, and its card falls back to the product default.
        if (collectionId) {
          const collectionIdValue = { type: 'String', value: String(collectionId), isCollection: false };
          f = f
            .addProductCategoryIdFilter('ImmediateParent', [collectionId])
            .addVariantDataFilter(`${rlwPrefix}_ShopifyCollectionIds`, c => c.addContainsCondition(collectionIdValue));
          // Member price collection (data-member-variants, set from the
          // collection metafield custom.member_price_variants): keep only
          // variants AX flags as having a member price, so the exploded card
          // variant is the member one instead of a sale variant. The key is the
          // same one the "Medlemspris" facet filters on; the flag matched the
          // price schedule on every variant checked 2026-10-13, but it can lag a
          // schedule change — the card's member price display still follows the
          // schedule. Typed Boolean DataValue for the same SDK reason as above.
          // Hidden on purpose (no chip), and inside rlwFetchListing so the first
          // page, sort, facets and pagination all agree. ClickUp 869fgh6tu.
          if (rlwState.memberVariantsOnly) {
            const memberFlagValue = { type: 'Boolean', value: true, isCollection: false };
            f = f.addVariantDataFilter(RLW_MEMBER_FLAG_KEY, c => c.addEqualsCondition(memberFlagValue));
          }
        }
        return rlwStoreScopeFilter(f);
      })
      .facets(f => {
        configs.forEach(cfg => {
          if (cfg.kind === 'price') {
            const active = facets[cfg.key];
            const low = active?.min ?? null;
            const high = active?.max ?? null;
            f.addSalesPriceRangeFacet(cfg.selectionStrategy, low, high);
          } else if (cfg.kind === 'range') {
            const active = facets[cfg.key];
            f.addProductDataDoubleRangeFacet(cfg.key, cfg.selectionStrategy, active?.min ?? null, active?.max ?? null);
          } else if (cfg.kind === 'boolean') {
            const active = facets[cfg.key];
            f.addProductDataBooleanValueFacet(cfg.key, cfg.selectionStrategy, typeof active === 'boolean' ? [active] : null);
          } else if (cfg.kind === 'number') {
            const selectedValues = facets[cfg.key];
            f.addProductDataDoubleValueFacet(cfg.key, cfg.selectionStrategy, selectedValues && selectedValues.length ? selectedValues : null);
          } else {
            const selectedValues = facets[cfg.key];
            f.addProductDataStringValueFacet(cfg.key, cfg.selectionStrategy, selectedValues && selectedValues.length ? selectedValues : null);
          }
        });
        // Member price collection: the variant filter above picks the card
        // variant, but Relewise counts the variant-level facets (Farve,
        // Materiale, Tilbud, price…) without variant filters — "Grå 302" led
        // to 262 products. The same restriction as a selected facet makes those
        // counts agree with the results, as the visible "Medlemspris" facet
        // does. Its pill is dropped in rlwGetFacetConfigs (869fgh6tu).
        if (collectionId && rlwState.memberVariantsOnly) {
          f.addProductDataBooleanValueFacet(RLW_MEMBER_FLAG_KEY, 'Variant', [true]);
        }
      })
      .pagination(p => p.setPageSize(perPage).setPage(page))
      .sorting(s => {
        switch (sorting) {
          case 'title-ascending':
            s.sortByProductAttribute('DisplayName', 'Ascending', n => n.sortByProductRelevance());
            break;
          case 'title-descending':
            s.sortByProductAttribute('DisplayName', 'Descending', n => n.sortByProductRelevance());
            break;
          // Price sorts go by the VARIANT's price, not the product's: the card
          // shows the exploded variant's price, while the product-level
          // SalesPrice is the cheapest variant's. A product with mixed variant
          // prices (e.g. Nerissa: 4.999 / 5.999) was ranked at its minimum but
          // displayed the popular variant's higher price — out of order.
          // Variant sorting also makes Relewise explode the cheapest (asc) /
          // dearest (desc) variant, so the sort key is the price on the card.
          case 'price-ascending':
            s.sortByProductVariantAttribute('SalesPrice', 'Ascending', n => n.sortByProductRelevance());
            break;
          case 'price-descending':
            s.sortByProductVariantAttribute('SalesPrice', 'Descending', n => n.sortByProductRelevance());
            break;
          case 'best-selling':
            s.sortByProductPopularity('Descending', n => n.sortByProductRelevance());
            break;
          case 'created-descending':
            s.sortByProductData(`${rlwPrefix}_CreatedAt`, 'Product', 'Descending', n => n.sortByProductRelevance());
            break;
          case 'created-ascending':
            s.sortByProductData(`${rlwPrefix}_CreatedAt`, 'Product', 'Ascending', n => n.sortByProductRelevance());
            break;
          default:
            s.sortByProductData('InStock', 'Product', 'Descending', n => n.sortByProductRelevance());
            break;
        }
      });

    if (term) productBuilder = productBuilder.setTerm(term);

    const productRequest = productBuilder.build();

    // See the ExplodedVariants comment above — the SDK bundled by the app
    // embed predates ProductSearchBuilder#setVariantRequestSettings, so (as
    // in rlwSearchDropdown) it's set directly on the built request instead.
    if (term) {
      productRequest.settings = {
        ...productRequest.settings,
        variantRequestSettings: { maxVariantsPerProduct: 1, sorting: 'ByRelevance' },
      };
    }

    const builder = new Relewise.SearchCollectionBuilder(settings)
      .addRequest(productRequest);

    const searcher = new Relewise.Searcher(
      window.relewiseAPI.relewise_dataid,
      window.relewiseAPI.relewise_api,
      { serverUrl: window.relewiseAPI.relewise_server_url }
    );

    return searcher.batch(builder.build());
  }

  // ─── Product card (JS-built FALLBACK) ─────────────────────────────────────
  // The grid renders the theme's server-side master card (see rlwRenderCards
  // below); this JS-built copy remains only as the per-slot fallback when a
  // native-card fetch fails (product unpublished, network error, missing
  // handle). It intentionally lags the master card feature-wise (no member
  // price, no campaign badges) — a plain but functional card beats a blank slot.
  //
  // Everything it CAN match, it must: badge markup, merchant label gates and
  // every user-facing string come from the same settings and locale keys the
  // master card uses (handed over by relewise-config.liquid). A fallback that
  // renders unstyled English on a Danish PLP reads as "the wrong product card"
  // — which is exactly how it was reported (ClickUp 869ekrzgh).

  // ─── Variant card images ──────────────────────────────────────────────────
  // Every Liquid card shows the variant's own image (product-card.liquid and
  // product-card-mini.liquid, gated on the global "Use first variant image as
  // card image" setting). The cards relewise.js builds in JS could not: the
  // Relewise catalog indexes `ImageUrls` at PRODUCT level only, so they kept
  // showing the product's featured image — routinely a lifestyle or
  // dimension-drawing shot in this PIM (ClickUp 869eq5zec).
  //
  // Three sources, cheapest first:
  //   1. a variant-level image key, IF the feed ever indexes one — free, no
  //      request. Both spellings are read (`ImageUrls` list, `ImageUrl` scalar):
  //      the product-level key is plural, the category one singular, and neither
  //      is requested at variant level today. So this is a no-op until the feed
  //      adds it, at which point it silently takes over from source 2.
  //   2. templates/product.variant-image.liquid, fetched per handle+variant.
  //      Shopify resolves `?variant=` into selected_or_first_available_variant,
  //      so the image comes from the theme's own rule rather than a second
  //      implementation of it, and it needs no Storefront API token, no catalog
  //      change and no external party. The variant asked for is the one the card
  //      ALREADY describes — Relewise explodes one variant per product and its
  //      price, badges and add-to-cart id all come from that variant — so image
  //      and price finally agree.
  //   3. the product-level `ImageUrls[0]` these cards used before — unchanged
  //      fallback whenever 1 and 2 come up empty.
  //
  // Source 2 also answers whether that image is a PACKSHOT (ClickUp 869f20g3c).
  // The Liquid mini card detects one by the alt-text marker and renders it
  // contain + padded via `.img--packshot`; the JS twin never saw the alt text,
  // so a background-free sofa in its 90-120px cover box lost both ends. The
  // lookup now runs the same `_packshot-image-class` / `_packshot-padding`
  // snippets server-side and returns `{url, packshot, pad}`, so the theme's
  // Liquid stays the single source of the rule and its settings. Only source 2
  // can carry the flag — the feed and the product-level fallback index bare
  // URLs — so a card on either of those keeps the cover crop it has today.
  //
  // Always primed as a batch BEFORE the cards are built, never during: the
  // builders stay synchronous, so nothing paints a product image and swaps it a
  // moment later, and the `_rlwMiniCardNoHandle` drain further down (which must
  // not have an await between build and flush) keeps working untouched.
  const RLW_VARIANT_IMAGE_CACHE_MAX = 300;
  const _rlwVariantImageCache = new Map();

  // Configurator verdict, carried by the same lookup (ClickUp 869f2ggg9).
  //
  // Kept in its OWN Map rather than as a field on the meta above, because that
  // cache's documented contract is "null whenever the card is not showing the
  // lookup's image" — a product with no image at all answers {"url":null,…} and
  // caches null, which would take the flag down with it.
  //
  // Keyed by HANDLE, not handle|variant: `template_suffix` is a product-level
  // property, so every exploded variant of a product gives the same answer.
  // That also makes this map no larger than the image one, so it can never be
  // the first of the two to hit its cap and clear — the image cache clearing
  // re-queues the fetch, which repopulates both.
  const RLW_CONFIGURATOR_CACHE_MAX = 300;
  const _rlwConfiguratorCache = new Map();

  // Shop price, carried by the same lookup (ClickUp 869f5wwgt): the resolved
  // variant's catalogue `price` / `compareAtPrice` in cents. The JS-built rows
  // printed the Relewise feed's salesPrice/listPrice, which drifts from the
  // catalogue — a cart recommendation showed a price no variant of the product
  // has. Its own Map for the configurator cache's reason: the image cache holds
  // null for a product with no image, and the price must survive that. Keyed
  // like the image cache (handle|variant — price is per variant) and always
  // written and cleared in step with it, so a cached image never leaves its
  // price behind.
  const _rlwShopPriceCache = new Map();

  // Badge inputs, carried by the same lookup (ClickUp 869fbuexz): the resolved
  // variant's Mix & Save discount map, price schedule and campaign references,
  // read from Shopify. The search dropdown read all three off the Relewise
  // feed, which returned many products with no variant and lagged a new Mix &
  // Save configuration by hours, so "Mix & spar" went missing where the product
  // page showed it. Own Map and keyed, written and cleared like the price cache.
  const _rlwShopBadgeCache = new Map();

  function rlwVariantImageEnabled() {
    return window.relewiseAPI.card_use_variant_image !== false;
  }

  /** Shopify handle of a Relewise product result, or ''. */
  function rlwHandleOf(product) {
    return product?.data?.[`${rlwPrefix}_ShopifyHandle`]?.value || '';
  }

  /** Digits-only Shopify variant id of the exploded variant, or ''. */
  function rlwVariantIdOf(product) {
    const id = product?.variant?.data?.[`${rlwPrefix}_ShopifyVariantId`]?.value;
    return id == null ? '' : String(id).replace(/\D/g, '');
  }

  /**
   * Variant to pin a listing card to, or '' for none (ClickUp 869f1nkau).
   *
   * The card always shows and links to the variant Relewise exploded, which is
   * also the one its price and add-to-cart id already come from:
   *   - search: the variant the query matched ("sofa grøn" returns the green
   *     variant) — the same rule rlwBuildSearchProductRow follows in the
   *     predictive-search dropdown;
   *   - collection: a variant that passed the request's variant filters and any
   *     active variant facet — so a variant collection such as "blue sofas"
   *     shows the blue variant rather than the product default. On a
   *     product-level collection it is Relewise's pick among its variants.
   *
   * Shares the "Use first variant image as card image" setting with the image
   * resolver, so one switch restores the product-level behaviour everywhere.
   * @param {object} product - Relewise product result.
   * @returns {string} Digits-only variant id, or '' to leave the card unpinned.
   */
  function rlwPinnedVariantId(product) {
    if (!rlwVariantImageEnabled()) return '';
    return rlwVariantIdOf(product);
  }

  /** Cache key: a product's image depends on which variant was exploded. */
  function rlwVariantImageKey(product) {
    const handle = rlwHandleOf(product);
    if (!handle) return '';
    return `${handle}|${rlwVariantIdOf(product)}`;
  }

  /** Source 1 — a variant-level image key, if the feed carries one. */
  function rlwVariantImageFromFeed(product) {
    const list = rlwDataValue(product?.variant?.data, `${rlwPrefix}_ImageUrls`);
    if (Array.isArray(list) && list[0]) return String(list[0]);
    const single = rlwDataValue(product?.variant?.data, `${rlwPrefix}_ImageUrl`);
    return single ? String(single) : '';
  }

  /**
   * Locale-aware root for the lookup URL. `rlwState.rootUrl` is only ever set by
   * rlwInitListing, and the three surfaces that prime images (complementary
   * block, cart drawer, search dropdown) all run on other templates — where it
   * stays '/' and would drop the locale prefix on a non-default market. Shopify's
   * own routes root carries that prefix on every page, so prefer it.
   * @returns {string} Root path with a trailing slash.
   */
  function rlwLocaleRoot() {
    const root = (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root)
      || rlwState.rootUrl
      || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function rlwVariantImageUrl(product) {
    const handle = rlwHandleOf(product);
    const variantId = rlwVariantIdOf(product);
    const base = `${rlwLocaleRoot()}products/${encodeURIComponent(handle)}?view=variant-image`;
    return variantId ? `${base}&variant=${variantId}` : base;
  }

  /**
   * Synchronous accessor for the card builders. Returns '' when nothing is
   * resolved, so every caller keeps its own product-level fallback.
   * @param {object} product - Relewise product result.
   * @returns {string} Image URL, or '' to fall back.
   */
  function rlwVariantImage(product) {
    if (!rlwVariantImageEnabled()) return '';
    const fromFeed = rlwVariantImageFromFeed(product);
    if (fromFeed) return fromFeed;
    const meta = rlwVariantImageMeta(product);
    return meta ? meta.url : '';
  }

  /**
   * The lookup's full answer for a product — `{url, packshot, pad}` — or null
   * whenever the card is NOT showing the lookup's image: feature off, the feed
   * carried an image (source 1 wins, and the fetch was never queued), or the
   * lookup missed / timed out. Same resolution order as rlwVariantImage, so a
   * non-null result always describes the URL that function returns.
   * @param {object} product - Relewise product result.
   * @returns {{url: string, packshot: boolean, pad: string}|null}
   */
  function rlwVariantImageMeta(product) {
    if (!rlwVariantImageEnabled()) return null;
    if (rlwVariantImageFromFeed(product)) return null;
    const key = rlwVariantImageKey(product);
    return (key && _rlwVariantImageCache.get(key)) || null;
  }

  /**
   * Record a product's configurator verdict from the lookup's answer.
   * @param {string} handle - Shopify handle the answer belongs to.
   * @param {boolean} isConfigurator - Whether its PDP is a configurator template.
   */
  function rlwCacheConfigurator(handle, isConfigurator) {
    if (!handle) return;
    if (_rlwConfiguratorCache.size >= RLW_CONFIGURATOR_CACHE_MAX) {
      _rlwConfiguratorCache.clear();
    }
    _rlwConfiguratorCache.set(handle, isConfigurator);
  }

  /**
   * Whether this product's PDP is a configurator template (ClickUp 869f2ggg9).
   *
   * Synchronous, like the image accessors, and false until the lookup has
   * answered — so a miss or the race timeout leaves the card with the
   * add-to-cart button it renders today. (The lookup now runs whatever the image
   * setting says and even when the feed carries an image, because it carries
   * the shop price too — ClickUp 869f5wwgt.) Failing to the
   * status quo rather than to a link is deliberate: a wrongly-suppressed
   * quick-add is a lost sale on a product that has nothing to configure.
   * @param {object} product - Relewise product result.
   * @returns {boolean}
   */
  function rlwIsConfigurator(product) {
    return _rlwConfiguratorCache.get(rlwHandleOf(product)) === true;
  }

  /**
   * The shop's catalogue price for the variant a Relewise result describes, in
   * MAJOR units to match the feed's salesPrice/listPrice, or null until the
   * lookup has answered (miss, timeout) — callers then keep the feed's prices.
   * Not gated on the image setting: which price is true has nothing to do with
   * which image the card shows.
   * @param {object} product - Relewise product result.
   * @returns {{salesPrice: number, listPrice: number|null}|null}
   */
  function rlwShopPrice(product) {
    const key = rlwVariantImageKey(product);
    const entry = key ? _rlwShopPriceCache.get(key) : null;
    if (!entry) return null;
    return {
      salesPrice: entry.price / 100,
      listPrice: entry.compareAtPrice > 0 ? entry.compareAtPrice / 100 : null,
    };
  }

  /**
   * Shopify's badge inputs for the variant a Relewise result describes, or null
   * until the lookup has answered with them (miss, timeout, a cached view from
   * before ClickUp 869fbuexz) — callers then keep the feed's. Inside an answer a
   * null `mixAndSave` / `schedule` is Shopify's "none", never a reason to read
   * the feed: the feed is the copy that lags.
   * @param {object} product - Relewise product result.
   * @returns {{mixAndSave: object|null, schedule: object|null, campaignIds: string[]}|null}
   */
  function rlwShopBadges(product) {
    const key = rlwVariantImageKey(product);
    return (key && _rlwShopBadgeCache.get(key)) || null;
  }

  /**
   * Resolve the variant image — and the shop price (ClickUp 869f5wwgt) — for a
   * batch of products before they are rendered. Never rejects: a miss, a 404,
   * an unparseable body and the timeout all cache `null`, which sends the
   * builder down its product-level image and feed-price fallbacks exactly as
   * before. Cached per handle+variant, so a search dropdown re-resolves nothing
   * as the shopper keeps typing.
   *
   * Runs whatever the image setting says and even when the feed carries its
   * own variant image: the price is needed regardless. The image accessors
   * still apply both rules, so neither changes which image a card shows.
   * @param {Array} products - Relewise product results.
   * @param {number} [timeoutMs] - Upper bound on how long a render may wait.
   * @returns {Promise<boolean>} Whether every lookup had landed when it resolved
   *   (false = the timeout won and some cards will paint the feed's price).
   */
  function rlwPrimeVariantImages(products, timeoutMs = 900) {
    return rlwRaceLookups(rlwLookupBatch(products), timeoutMs);
  }

  /**
   * Cap a lookup batch at `timeoutMs`. A slow lookup must degrade to the
   * product image and the feed's price, never hold an empty carousel or a
   * search dropdown open on a spinner.
   * @param {Promise<void>} settled - From rlwLookupBatch.
   * @param {number} timeoutMs - Upper bound on the wait.
   * @returns {Promise<boolean>} true when the batch landed first.
   */
  function rlwRaceLookups(settled, timeoutMs) {
    return Promise.race([
      settled.then(() => true),
      new Promise(resolve => { setTimeout(() => resolve(false), timeoutMs); }),
    ]);
  }

  /**
   * Fetch the lookup for every product not already cached (see
   * rlwPrimeVariantImages). Split out so the search dropdown can keep the
   * uncapped promise and re-render once late lookups land.
   * @param {Array} products - Relewise product results.
   * @returns {Promise<void>} Settles when every queued lookup has; never rejects.
   */
  function rlwLookupBatch(products) {
    if (!Array.isArray(products) || !products.length) {
      return Promise.resolve();
    }

    const pending = [];
    const queued = new Set();

    products.forEach(product => {
      const key = rlwVariantImageKey(product);
      if (!key || queued.has(key) || _rlwVariantImageCache.has(key)) return;
      queued.add(key);
      const handle = rlwHandleOf(product);
      pending.push(
        fetch(rlwVariantImageUrl(product))
          .then(res => (res.ok ? res.text() : null))
          .then(text => {
            // Cache the whole answer, not just the URL: the packshot flag and
            // its padding ride along for rlwVariantImageMeta. `null` for any
            // body without a usable url — the same fallback signal as before.
            let meta = null;
            let price = null;
            let badges = null;
            try {
              const parsed = text ? JSON.parse(text) : null;
              if (parsed && parsed.url) {
                meta = {
                  url: String(parsed.url),
                  packshot: parsed.packshot === true,
                  pad: typeof parsed.pad === 'string' ? parsed.pad : '',
                };
              }
              // Read off `parsed`, not `meta`: the configurator verdict is
              // product-level and says nothing about the image, so a product
              // with no image at all ({"url":null,…}) must still keep it.
              if (parsed) rlwCacheConfigurator(handle, parsed.configurator === true);
              // Same for the price; absent on a cached older view → feed price.
              if (parsed && Number.isFinite(parsed.price)) {
                price = {
                  price: parsed.price,
                  compareAtPrice: Number.isFinite(parsed.compareAtPrice) ? parsed.compareAtPrice : 0,
                };
              }
              // Badge inputs (ClickUp 869fbuexz). The key's presence is what
              // says this view answered — a cached older view lacks it and
              // leaves the feed's — so a null value is kept as Shopify's "none".
              if (parsed && Object.prototype.hasOwnProperty.call(parsed, 'mixAndSave')) {
                const isObject = v => !!v && typeof v === 'object' && !Array.isArray(v);
                badges = {
                  mixAndSave: isObject(parsed.mixAndSave) ? parsed.mixAndSave : null,
                  schedule: isObject(parsed.schedule) ? parsed.schedule : null,
                  campaignIds: Array.isArray(parsed.campaignIds) ? parsed.campaignIds.map(String) : [],
                };
              }
            } catch {
              meta = null;
              price = null;
              badges = null;
            }
            if (_rlwVariantImageCache.size >= RLW_VARIANT_IMAGE_CACHE_MAX) {
              _rlwVariantImageCache.clear();
              _rlwShopPriceCache.clear();
              _rlwShopBadgeCache.clear();
            }
            _rlwVariantImageCache.set(key, meta);
            _rlwShopPriceCache.set(key, price);
            _rlwShopBadgeCache.set(key, badges);
          })
          .catch(() => {
            _rlwVariantImageCache.set(key, null);
            _rlwShopPriceCache.set(key, null);
            _rlwShopBadgeCache.set(key, null);
          })
      );
    });

    return Promise.all(pending).then(() => undefined);
  }

  function rlwBuildCard(product) {
    const cfg = window.relewiseAPI;

    const handleData = product.data?.[`${rlwPrefix}_ShopifyHandle`];
    const handle = handleData?.value || '';
    // Always the bare product URL — never collection-scoped. A collection in
    // the PDP URL makes a third-party app pull in the whole collection's data,
    // so cards link to /products/<handle> only (theme setting
    // card_url_within_coll is off for the same reason).
    let url = handle ? `${rlwState.rootUrl}products/${handle}` : '#';
    // Search results (and variant-facet-filtered collections) deep-link to the
    // matched variant, same gate as the master card above (ClickUp 869f1nkau),
    // so the degraded card lands the shopper on the variant they asked for. The image below stays
    // product-level — the 600px-vs-240px trade-off documented there is
    // unchanged, so this card can still show a different colour than it links
    // to. One more way the fallback lags the master card.
    const pinnedVariantId = handle ? rlwPinnedVariantId(product) : '';
    if (pinnedVariantId) url += `?variant=${pinnedVariantId}`;

    const imageUrlsData = product.data?.[`${rlwPrefix}_ImageUrls`];
    // Deliberately NOT on the variant-image resolver (ClickUp 869eq5zec). This
    // card renders its image at 600px, the resolver's lookup returns a 240px
    // URL sized for the 72-120px mini cards, and priming 16-24 products per grid
    // render to serve a card that only appears when a master-card fetch has
    // already failed is the wrong trade. It keeps the catalog's full-resolution
    // URL — one more way this fallback lags the master card, as above.
    const imageUrl = imageUrlsData?.value?.$values?.[0] || '';

    const title = product.displayName || '';
    const isAvailable = product.data?.IsAvailable?.value !== false;
    const salesPrice = product.salesPrice;
    const listPrice = product.listPrice;
    // Zero price = missing catalog data, not purchasable (task 869eba8mv):
    // no price, no sale label, no quick-add — mirrors the Liquid card gating.
    const zeroPrice = !(salesPrice > 0);
    const isOnSale = !zeroPrice && listPrice != null && listPrice > salesPrice;

    // Card outer classes
    const cardClasses = [
      'card card--product hover:grow-media h-full relative flex',
      cfg.card_contain ? 'card--product-contained' : '',
      cfg.show_dividers === false ? 'card--no-lines' : '',
    ].filter(Boolean).join(' ');

    // card__info-inner class — always a stretched flex column, mirroring the
    // master card's ILVA fixed-slot layout (independent of card_price_bottom).
    const infoInnerClass = 'card__info-inner flex flex-col h-full w-full';

    // Title class
    const titleClass = `card__title font-bold${cfg.show_dividers === false ? ' mt-1 mb-0' : ' mb-1'}`;

    const imageInner = imageUrl
      ? `<img class="img-fit card__main-image" src="${rlwEscape(rlwSquareImageAt(imageUrl, 600))}"${rlwSquareImageSrcset(imageUrl, RLW_CARD_WIDTHS, RLW_CARD_SIZES)} alt="${rlwEscape(title)}" loading="lazy" width="600" height="600">`
      : `<div class="media__placeholder img-fit"></div>`;

    // Labels — same container/wrapper/badge markup product-label.liquid emits for
    // a card, so the theme's .product-label CSS applies. (The classes this used
    // to emit — .card__labels / .label--sale — exist nowhere in the theme's CSS,
    // so a fallback card's badge rendered as bare unstyled text.) Merchant gates
    // and locale strings mirror rlwBuildSearchProductRow below; keep the two in
    // sync.
    const labels = cfg.labels || {};
    const labelPosition = cfg.product_label_card_position;
    let badgeHtml = '';
    if (!isAvailable && cfg.show_sold_out_label !== false && labels.sold_out) {
      badgeHtml = `<span class="product-label product-label--sold-out">${rlwEscape(labels.sold_out)}</span>`;
    } else if (isOnSale && cfg.show_sale_label !== false) {
      let saleText = '';
      if (cfg.sale_label_type === 'percent' && labels.percent_reduction) {
        const pct = Math.round((1 - salesPrice / listPrice) * 100);
        saleText = rlwEscape(labels.percent_reduction).replace('__amount__', pct);
      } else if (cfg.sale_label_type === 'money' && labels.value_reduction) {
        // rlwMoney can carry markup from the shop's money format — injected raw,
        // same as the price spans below.
        saleText = rlwEscape(labels.value_reduction).replace('__amount__', rlwMoney(listPrice - salesPrice));
      } else if (labels.sale) {
        saleText = rlwEscape(labels.sale);
      }
      if (saleText) {
        badgeHtml = `<span class="product-label product-label--sale">${saleText}</span>`;
      }
    } else if (rlwShowNyhed(product) && labels.new) {
      // Last, mirroring the Liquid order — the New badge is the last block in
      // product-label.liquid. ClickUp 869f3974k.
      badgeHtml = `<span class="product-label product-label--new">${rlwEscape(labels.new)}</span>`;
    }

    let labelsHtml = '';
    if (badgeHtml && labelPosition !== 'none') {
      const positionClass = labelPosition ? ` absolute ${rlwEscape(labelPosition)}` : '';
      const justifyClass = (labelPosition || '').includes('end') ? ' justify-end' : '';
      labelsHtml = `<div class="product-label-container${positionClass} product-label-container--card">`
        + `<div class="product-label-wrapper flex${justifyClass}">${badgeHtml}</div></div>`;
    }

    // Price HTML — mirrors the native price snippet structure
    let priceHtml = '';
    if (salesPrice != null && !zeroPrice) {
      const priceClass = 'price--bottom';
      const regularLabel = rlwEscape(labels.regular_price || '');
      // The struck price and the "Spar X" line are one unit on the master card
      // (price.liquid renders .price__savings-text inside .price__sale-details
      // whenever show_savings is set — which product-card.liquid always sets).
      // Dropping it here made a fallback card in a sale row read as a different
      // card design. Only the schedule layer's .card-price--hide-savings
      // suppresses it, and the fallback has no schedule.
      const savingsHtml = labels.savings
        ? `<span class="price__savings-text">`
          + `${rlwEscape(labels.savings).replace('__amount__', rlwMoney(listPrice - salesPrice))}</span>`
        : '';
      const wasHtml = isOnSale
        ? `<div class="price__sale-details">
             <span class="price__was">
               <span class="visually-hidden js-label">${regularLabel}</span>
               <span class="js-value">${rlwMoney(listPrice)}</span>
             </span>
             ${savingsHtml}
           </div>`
        : `<span class="price__was"><span class="visually-hidden js-label"></span><span class="js-value"></span></span>`;
      priceHtml = `
        <div class="price${isOnSale ? ' price--on-sale' : ''} ${priceClass}">
          <div class="price__default">
            <span class="price__current">
              <span class="visually-hidden js-label">${isOnSale ? rlwEscape(labels.sale_price || '') : regularLabel}</span>
              <span class="js-value">${rlwMoney(salesPrice)}</span>
            </span>
            ${wasHtml}
          </div>
          <div class="unit-price relative" hidden></div>
        </div>`;
    }

    // Price position — the master card's fixed slots: bottom-anchored price
    // slot plus an (empty — no rich-info data here) swatch slot, so a fallback
    // card in a row doesn't shift the price line out of alignment.
    const priceBottom = `
      <div class="card__price-anchor flex grow items-end">
        <div class="card__price-slot w-full">${priceHtml}</div>
      </div>
      <div class="card__swatch-slot"></div>`;

    // Vendor — try standard Brand property first, fall back to data key
    let vendorHtml = '';
    if (cfg.card_show_vendor) {
      const vendor = product.brand?.displayName
        || product.data?.['Vendor']?.value
        || '';
      if (vendor) {
        const vendorClass = `card__vendor${cfg.show_dividers === false ? ' mb-0' : ' mb-1'} text-sm text-theme-light`;
        vendorHtml = `<p class="${vendorClass}">${rlwEscape(vendor)}</p>`;
      }
    }

    // In-stock indicator
    let inventoryHtml = '';
    if (cfg.card_show_inventory && isAvailable && labels.in_stock) {
      const inventoryClass = cfg.show_dividers === false ? 'pt-5' : 'mt-3 border-top pt-3';
      inventoryHtml = `<div class="${inventoryClass} text-xs product-inventory__status" data-inventory-level="in_stock">${rlwEscape(labels.in_stock)}</div>`;
    }

    // Quick-add
    const variantId = product.variant?.data?.[`${rlwPrefix}_ShopifyVariantId`]?.value;
    let quickAddHtml = '';
    if (cfg.enable_quick_add !== false && variantId && isAvailable && !zeroPrice) {
      const addToCartLabel = rlwEscape(labels.add_to_cart || '');
      const atcMobile = cfg.card_atc_mobile || 'icon';
      const atcDesktop = cfg.card_atc_desktop || 'icon';
      const quickAddClasses = [
        'card__quick-add',
        atcMobile === 'text_button_bottom' ? 'mob:card__quick-add--below' : '',
        atcMobile === 'icon' ? 'mob:card__quick-add--icon' : '',
        atcDesktop === 'text_button_bottom' ? 'desktop:card__quick-add--below' : '',
        atcDesktop === 'icon' ? 'desktop:card__quick-add--icon' : '',
      ].filter(Boolean).join(' ');

      quickAddHtml = `
        <div class="${quickAddClasses}">
          <product-form>
            <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
            <form method="post" action="/cart/add" class="js-product-form" novalidate>
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${rlwEscape(String(variantId))}">
              <button class="btn btn--primary w-full" name="add" aria-haspopup="dialog">
                <span class="quick-add-btn-icon">
                  <span class="visually-hidden">${addToCartLabel}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon">
                    <path d="M10 5.18327C11.6542 5.18327 13 6.5291 13 8.18327H13.7917C13.7917 6.0916 12.0917 4.3916 10 4.3916C7.90833 4.3916 6.20833 6.0916 6.20833 8.18327H6.99999C6.99999 6.5291 8.34583 5.18327 10 5.18327Z" fill="currentColor"/>
                    <path d="M15.0875 14.4623H4.9125L3.85 8.18311H3.05L4.24584 15.2539H15.7542L16.95 8.18311H16.15L15.0875 14.4623Z" fill="currentColor"/>
                  </svg>
                </span>
                <span class="quick-add-btn-text">${addToCartLabel}</span>
              </button>
              <a href="${rlwEscape(url)}" class="btn btn--secondary quick-add-view-btn">${rlwEscape(labels.view_details || '')}</a>
            </form>
          </product-form>
        </div>`;
    }

    // Tagged so "are we showing the right cards?" is one selector to answer in
    // the browser — a fallback card is visibly plainer than the master card
    // (no swatches, no member price, no campaign badge) and used to be
    // indistinguishable from a master card starved of catalog data.
    const li = document.createElement('li');
    li.dataset.rlwFallbackCard = '';
    li.innerHTML = `
      <product-card class="${cardClasses}" data-product-id="${rlwEscape(String(product.productId))}">
        <div class="card__media relative">
          <a href="${rlwEscape(url)}" aria-label="${rlwEscape(title)}" class="media block relative js-prod-link" style="padding-top: 100%;" tabindex="-1">
            ${imageInner}
          </a>
          ${labelsHtml}
        </div>
        <div class="card__info-container flex flex-col flex-auto relative">
          <div class="card__info w-full">
            <div class="${infoInnerClass}">
              ${vendorHtml}
              <p class="${titleClass}">
                <a href="${rlwEscape(url)}" class="card-link text-current js-prod-link">${rlwEscape(title)}</a>
              </p>
              ${inventoryHtml}
              ${priceBottom}
            </div>
          </div>
          ${quickAddHtml}
        </div>
      </product-card>`;

    return li;
  }

  // ─── Native product card (server-rendered master card) ────────────────────
  // Each grid slot fetches the theme's master product card via the
  // product.relewise-product-card view template ({% layout none %}), so card
  // content and logic — member price schedule, campaign badges, quick buy,
  // swatches, labels — live in ONE place and reach the collection grid
  // automatically. The fetch URL is collection-scoped so the global
  // `collection` object is in scope inside the template and the card's links
  // keep `| within: collection` (breadcrumbs, prev/next on the PDP).

  /**
   * @param {string} handle - Product handle.
   * @param {string} [variantId] - Digits-only variant id to pin the card to,
   *   from rlwPinnedVariantId(). Omitted/'' renders the card's default variant.
   *   _rlwCardHtmlCache is keyed by this URL, so it is variant-aware for free.
   *
   *   A pinned id switches to the `relewise-product-card-variant` VIEW, not
   *   just a `&variant=` on the usual one. The plain view is also fetched by
   *   the Relewise app's own consumers (the Product Category block via
   *   relewise_buildProductViewUrl, and the recommendation UI Components),
   *   whose URLs may carry a `?variant=` of their own — so reading the query
   *   param there would have pinned their cards too. The view name is the one
   *   signal only this caller controls, which keeps the pin scoped to this
   *   grid by construction (ClickUp 869f1nkau).
   * @returns {string} Fetch URL for the native card template.
   */
  function rlwNativeCardUrl(handle, variantId) {
    const base = rlwState.collectionHandle
      ? `${rlwState.rootUrl}collections/${rlwState.collectionHandle}/products/${handle}`
      : `${rlwState.rootUrl}products/${handle}`;
    if (!variantId) return `${base}?view=relewise-product-card`;
    return `${base}?view=relewise-product-card-variant&variant=${variantId}`;
  }

  /**
   * Fetch URL for the native MINI card — the complementary row's twin of
   * rlwNativeCardUrl above (ClickUp 869f58rr2).
   *
   * Deliberately NOT collection-scoped: this row runs on product and cart
   * templates, and product-card-mini emits no `| within: collection` links, so
   * there is no collection context to preserve. And rlwLocaleRoot() rather than
   * rlwState.rootUrl for the same reason the variant-image primer uses it —
   * rlwState.rootUrl is only ever set by rlwInitListing, so on this row it
   * stays '/' and would drop the locale prefix on a non-default market.
   *
   * One view rather than a plain/variant pair: nothing but these rows request
   * it, so there is no foreign consumer whose own `?variant=` could get pinned
   * by accident — see templates/product.relewise-mini-card.liquid. The cart
   * rows ask for its cart twin, which renders the same card without the
   * wishlist heart and the energy mark (ClickUp 869f5wwgt).
   *
   * @param {string} handle - Shopify product handle.
   * @param {string} variantId - Digits-only variant id to pin the card to, or ''.
   * @param {boolean} [cart] - Cart row ("Fuldend dit køb"): the cart view.
   * @returns {string} Fetch URL for the native mini-card template.
   */
  function rlwMiniCardUrl(handle, variantId, cart = false) {
    const view = cart ? 'relewise-mini-card-cart' : 'relewise-mini-card';
    const base = `${rlwLocaleRoot()}products/${encodeURIComponent(handle)}?view=${view}`;
    return variantId ? `${base}&variant=${variantId}` : base;
  }

  function rlwFetchNativeCard(url, signal) {
    if (_rlwCardHtmlCache.has(url)) return Promise.resolve(_rlwCardHtmlCache.get(url));
    return fetch(url, { signal }).then(res => {
      if (!res.ok) return null;
      return res.text().then(html => {
        // A real card response is layout-free and carries the native-card
        // wrapper; anything else (a redirect to a full page, a password page)
        // counts as a miss so the slot falls back to the JS-built card.
        if (!html || !html.includes('data-rlw-native-card')) return null;
        if (_rlwCardHtmlCache.size >= RLW_CARD_CACHE_MAX) _rlwCardHtmlCache.clear();
        _rlwCardHtmlCache.set(url, html);
        return html;
      });
    });
  }

  function rlwSkeletonSlot() {
    return _rlwSkeletonLi ? _rlwSkeletonLi.cloneNode(true) : document.createElement('li');
  }

  // ─── Promotion blocks ───────────────────────────────────────────────────────
  // Merchant-configured "Wide/Media/Card promotion" section blocks are
  // pre-rendered server-side into hidden <template data-rlw-promo> nodes (same
  // Liquid snippets the native grid uses) so we don't have to reimplement their
  // markup in JS — we only decide where each one lands among the fetched cards.

  function rlwGetPromoBlocks(wrapper) {
    if (wrapper._rlwPromoBlocks) return wrapper._rlwPromoBlocks;
    const templates = wrapper.querySelectorAll('template[data-rlw-promo]');
    wrapper._rlwPromoBlocks = Array.from(templates).map(tpl => ({
      type: tpl.dataset.type,
      position: tpl.dataset.type === 'wide-promotion' ? tpl.dataset.position : parseInt(tpl.dataset.position, 10),
      hideOnFilter: tpl.dataset.hideOnFilter === 'true',
      template: tpl,
    }));
    return wrapper._rlwPromoBlocks;
  }

  function rlwHasActiveFacets() {
    return Object.keys(rlwState.activeFacets).some(key => rlwCountActiveFacets(key) > 0);
  }

  // Promotions only apply to the first page of results, mirroring the native
  // grid's `paginate.current_page == 1` check.
  function rlwBuildGridItems(products, wrapper) {
    const promoBlocks = rlwGetPromoBlocks(wrapper);
    if (rlwState.currentPage !== 1 || promoBlocks.length === 0) {
      return products.map(product => ({ kind: 'product', product }));
    }

    const filtersActive = rlwHasActiveFacets();
    const visible = block => !(filtersActive && block.hideOnFilter);
    const items = [];

    promoBlocks
      .filter(b => b.type === 'wide-promotion' && b.position === 'top' && visible(b))
      .forEach(b => items.push({ kind: 'promo', block: b }));

    // Positioned promos (image/card) insert before the product at that index,
    // and only when page 1 has a product there — the native grid's rule — so a
    // short list never ends on promo tiles. A position above "Products per
    // page" therefore never shows.
    const positioned = promoBlocks.filter(b => b.type !== 'wide-promotion' && visible(b));

    products.forEach((product, idx) => {
      const productIndex = idx + 1;
      positioned.forEach(b => {
        if (b.position === productIndex) items.push({ kind: 'promo', block: b });
      });
      items.push({ kind: 'product', product });
    });

    promoBlocks
      .filter(b => b.type === 'wide-promotion' && b.position === 'bottom' && visible(b))
      .forEach(b => items.push({ kind: 'promo', block: b }));

    return items;
  }

  /**
   * Fills one grid slot with the theme's master card, fetched per product from
   * Shopify (`?view=relewise-product-card`), or the JS-built fallback card when
   * that fetch misses. The grid's original path, and the fallback for any card
   * the client-side renderer declines.
   * @returns {Promise<void>}
   */
  function rlwFillNativeSlot(li, product, handle, signal, seq, fellBack) {
    return rlwFetchNativeCard(rlwNativeCardUrl(handle, rlwPinnedVariantId(product)), signal)
      .then(html => {
        if (seq !== _rlwFetchSeq) return;
        if (html) {
          // Fresh <li> instead of reusing the skeleton node — the skeleton
          // carries rlw-skeleton-card styling and aria-hidden.
          const fresh = document.createElement('li');
          fresh.innerHTML = html;
          li.replaceWith(fresh);
          rlwRevealWhenImageReady(fresh);
        } else {
          fellBack.push(handle);
          li.replaceWith(rlwBuildCard(product));
        }
      })
      .catch(err => {
        if (err.name === 'AbortError' || seq !== _rlwFetchSeq) return;
        fellBack.push(handle);
        li.replaceWith(rlwBuildCard(product));
      });
  }

  // ─── Client-side cards ────────────────────────────────────────────────────
  // Collection grid only, behind settings.relewise_client_cards. Instead of one
  // Shopify request per card, each card is built in the browser
  // (assets/relewise-cards.js) from the Relewise result the grid already has,
  // then corrected by ONE background request per 20 cards
  // (templates/search.relewise-card-data.liquid) carrying what Relewise cannot
  // sync: template suffix, has_only_default_variant, the exact hero/hover
  // images, fabric swatches, energy files, highlight, and the card variant's
  // catalogue price as a freshness check. Until that lands a card shows no
  // quick-add control — a configurator must never flash a buy button.
  //
  // Anything the renderer can't reproduce faithfully — per page (settings) or
  // per card (the background data's `unsupported` list, a product Shopify
  // doesn't resolve) — goes down rlwFillNativeSlot instead.

  // `all_products` resolves at most 20 unique handles per Liquid render.
  const RLW_CARD_DATA_BATCH = 20;
  const RLW_CARD_DATA_CACHE_MAX = 300;
  const _rlwCardDataCache = new Map();
  let _rlwCardCtx;

  /** Page-level card context (snippets/relewise-card-context.liquid), or null. */
  function rlwCardContext() {
    if (_rlwCardCtx !== undefined) return _rlwCardCtx;
    _rlwCardCtx = null;
    const el = document.getElementById('rlw-card-context');
    if (!el) return null;
    try {
      _rlwCardCtx = JSON.parse(el.textContent);
    } catch (e) {
      // Invalid context: the grid keeps the Shopify card fetch.
    }
    return _rlwCardCtx;
  }

  /** True when this page renders collection cards client-side. */
  function rlwClientCardsEnabled() {
    if (rlwState.searchTerm) return false;
    const ctx = rlwCardContext();
    if (!ctx || !window.RelewiseCards) return false;
    return window.RelewiseCards.unsupportedContext(ctx).length === 0;
  }

  /**
   * Relewise data keys the card model reads, beyond what the grid already
   * selects. Product-level metafields are asked for at variant level too (and
   * vice versa) where the Liquid card reads variant-then-product, so a field
   * Perfion moves between levels keeps working.
   */
  function rlwCardDataKeys() {
    const k = key => `${rlwPrefix}_${key}`;
    return {
      product: [
        'ProductType',
        k('ShopifyProductId'),
        k('perfion.title_line_1'),
        k('perfion.title_line_2'),
        k('custom.badges'),
        k('perfion.energiklasse'),
        k('perfion.fast_lav_pris'),
      ],
      variant: [
        k('AvailableForSale'),
        k('ilva.price_schedule'),
        k('ilva.related_campaigns'),
        k('ilva.mix_and_save'),
        k('perfion.fast_lav_pris'),
        k('perfion.energiklasse'),
      ],
    };
  }

  /** true / false / null from a Relewise boolean (true or "true" on the wire). */
  function rlwDataBool(data, key) {
    const v = rlwDataValue(data, key);
    if (v === true || v === 'true') return true;
    if (v === false || v === 'false') return false;
    return null;
  }

  /** A trimmed string value, or ''. */
  function rlwDataString(data, key) {
    const v = rlwDataValue(data, key);
    return v == null || typeof v === 'object' ? '' : String(v).trim();
  }

  /**
   * One card image record from an image value — a Relewise image URL string, or
   * a Storefront API Image ({url, altText, width, height}) — or null when it
   * carries no usable URL (a bare GID). `src` is asked for at 460px, as
   * image.liquid's `src_width` does.
   */
  function rlwCardImageFrom(entry) {
    if (!entry) return null;
    let src = '', alt = '', width = 0, height = 0;
    if (typeof entry === 'string') {
      src = entry;
    } else if (typeof entry === 'object') {
      const img = entry.preview_image || entry.previewImage || entry.image || entry;
      src = img.url || img.src || img.originalSrc || entry.url || '';
      alt = entry.alt || entry.altText || img.alt || img.altText || '';
      width = Number(img.width) || 0;
      height = Number(img.height) || 0;
    }
    if (!src || src.startsWith('gid://') || !/^(https?:)?\/\//i.test(src)) return null;
    const u = rlwResizableUrl(src);
    if (u) {
      u.searchParams.set('width', '460');
      src = src.startsWith('//') ? `//${u.host}${u.pathname}${u.search}` : u.href;
    }
    return { src, alt, packAlt: alt, width: width || 1200, aspectRatio: width && height ? width / height : 1 };
  }

  /** Collection-scoped card URL, mirroring product-card.liquid:37-58. */
  function rlwCardUrl(handle, variantId, hasOnlyDefaultVariant, ctx) {
    const base = rlwState.collectionHandle && ctx.settings.card_url_within_coll
      ? `${rlwState.rootUrl}collections/${rlwState.collectionHandle}/products/${handle}`
      : `${rlwState.rootUrl}products/${handle}`;
    return variantId && !hasOnlyDefaultVariant ? `${base}?variant=${variantId}` : base;
  }

  /** Campaign presentation for the given metaobject ids (relewiseAPI.campaigns). */
  function rlwCardCampaigns(ids) {
    const lookup = Array.isArray(window.relewiseAPI.campaigns) ? window.relewiseAPI.campaigns : [];
    const digits = v => String(v == null ? '' : v).replace(/\D/g, '');
    const wanted = new Set(ids.map(digits).filter(Boolean));
    return lookup.filter(c => wanted.has(digits(c.id)));
  }

  /**
   * The provisional card model — everything the Relewise result can supply.
   * Fields only Shopify knows keep safe defaults (no purchase control, no
   * swatches, no highlight) until rlwMergeCardData fills them.
   * @returns {object|null} Model, or null when the result lacks a handle or
   *   variant id (the card then takes the Shopify fetch).
   */
  function rlwCardModel(product, ctx) {
    const handle = rlwHandleOf(product);
    const variantId = rlwVariantIdOf(product);
    if (!handle || !variantId) return null;
    const pd = product.data, vd = product.variant?.data;
    const k = key => `${rlwPrefix}_${key}`;

    const salesPrice = rlwPrice(product.variant?.salesPrice);
    const listPrice = rlwPrice(product.variant?.listPrice);
    const priceCents = salesPrice == null ? null : Math.round(salesPrice * 100);
    const listCents = listPrice == null ? null : Math.round(listPrice * 100);

    let fastLowPrice = rlwDataBool(vd, k('perfion.fast_lav_pris'));
    if (fastLowPrice === null) fastLowPrice = rlwDataBool(pd, k('perfion.fast_lav_pris'));

    // Relewise's own image: the product's first image URL. The variant's image
    // arrives from the Storefront lookup (rlwFetchCardMedia) — Relewise syncs
    // dam.variant_associated_assets only as MediaImage GIDs, with no URL.
    const productImage = rlwCardImageFrom(rlwDataList(pd, k('ImageUrls'))[0]);
    const pinnedId = rlwPinnedVariantId(product);
    const available = rlwDataBool(pd, 'IsAvailable');
    const variantAvailable = rlwDataBool(vd, k('AvailableForSale'));

    return {
      id: rlwDataString(pd, k('ShopifyProductId')) || String(product.productId || ''),
      handle,
      pinnedVariantId: pinnedId,
      url: rlwCardUrl(handle, pinnedId, false, ctx),
      title: product.displayName || '',
      titleLine1: rlwDataString(pd, k('perfion.title_line_1')),
      titleLine2: rlwDataString(pd, k('perfion.title_line_2')),
      vendor: rlwDataString(pd, 'Vendor'),
      type: rlwDataString(pd, 'ProductType'),
      templateSuffix: '',
      hasOnlyDefaultVariant: false,
      purchaseKnown: false,
      available: available !== false,
      highlight: false,
      customBadge: rlwDataString(pd, k('custom.badges')),
      newLabel: rlwShowNyhed(product),
      fabricSwatches: [],
      colorCount: 0,
      colorsCountText: '',
      variantCountText: '',
      energy: (() => {
        const cls = rlwDataString(vd, k('perfion.energiklasse')) || rlwDataString(pd, k('perfion.energiklasse'));
        return cls ? { class: cls, fullsizeUrl: '', datasheetUrl: '' } : null;
      })(),
      media: { hero: productImage, hover: null, quality: 1 },
      variant: {
        id: variantId,
        price: priceCents,
        compareAtPrice: listCents != null && priceCents != null && listCents > priceCents ? listCents : null,
        available: variantAvailable !== false,
        schedule: ctx.settings.enable_member_prices ? rlwDataJson(vd, k('ilva.price_schedule')) : null,
        fastLowPrice: fastLowPrice === true,
        mixAndSave: rlwDataJson(vd, k('ilva.mix_and_save')),
        campaigns: rlwCardCampaigns(rlwDataList(vd, k('ilva.related_campaigns'))),
      },
      unsupported: [],
    };
  }

  /**
   * Folds the background card data into a model. Shopify wins every field it
   * sends. Returns null when the card must take the Shopify fetch instead: the
   * product didn't resolve, or Shopify resolved a different card variant.
   */
  function rlwMergeCardData(model, data, ctx) {
    if (!data || !data.variant) return null;
    if (String(data.variant.id) !== String(model.variant.id)) return null;
    const hasOnlyDefaultVariant = data.hasOnlyDefaultVariant === true;
    return {
      ...model,
      id: data.id || model.id,
      title: data.title || model.title,
      type: data.type ?? model.type,
      url: rlwCardUrl(model.handle, model.pinnedVariantId, hasOnlyDefaultVariant, ctx),
      templateSuffix: data.templateSuffix || '',
      hasOnlyDefaultVariant,
      purchaseKnown: true,
      available: data.available === true,
      highlight: data.highlight === true,
      newLabel: data.newLabel === true,
      fabricSwatches: Array.isArray(data.swatches) ? data.swatches : [],
      colorCount: Number(data.colorCount) || 0,
      colorsCountText: data.colorsCountText || '',
      // Absent in a data view cached before 869ffr22n → no line, as before.
      variantCountText: data.variantCountText || '',
      energy: data.energy || null,
      media: {
        hero: data.media?.hero || null,
        hover: data.media?.hover || null,
        slides: Array.isArray(data.media?.slides) ? data.media.slides : [],
        quality: Number(data.media?.quality) || 1,
      },
      variant: {
        ...model.variant,
        price: data.variant.price,
        compareAtPrice: data.variant.compareAtPrice,
        available: data.variant.available === true,
        // Liquid half of the "Udsolgt online" quick-add check (card-purchasability.js);
        // relewise-cards.js quickAdd() hands them to <card-purchasability>.
        inventoryTracked: data.variant.inventoryTracked === true,
        inventoryPolicy: data.variant.inventoryPolicy || '',
        campaigns: rlwCardCampaigns(data.variant.campaignIds || []),
        // Mix & Save and the schedule that decides its member suppression
        // (ClickUp 869fbuexz): the feed lagged a new configuration by hours.
        // Only when this view version sent the key — a cached older one keeps
        // the feed's; a null from Shopify means none.
        ...('mixAndSave' in data.variant ? { mixAndSave: data.variant.mixAndSave || null } : {}),
        ...('schedule' in data.variant
          ? { schedule: ctx.settings.enable_member_prices ? data.variant.schedule || null : null }
          : {}),
      },
      unsupported: Array.isArray(data.unsupported) ? data.unsupported : [],
    };
  }

  /** The background-request term for a card: `handle` or `handle:variantId`. */
  function rlwCardDataTerm(model) {
    return model.pinnedVariantId ? `${model.handle}:${model.pinnedVariantId}` : model.handle;
  }

  /**
   * Background card data for the given terms, batched RLW_CARD_DATA_BATCH per
   * request and cached for the session like the card HTML (a filter change
   * re-shows the same products). Resolves to Map(term → data|null); a failed
   * batch maps its terms to null, which sends those cards to the Shopify fetch.
   */
  function rlwFetchCardData(terms, signal) {
    const result = new Map();
    const missing = [];
    terms.forEach(t => {
      if (_rlwCardDataCache.has(t)) result.set(t, _rlwCardDataCache.get(t));
      else if (!missing.includes(t)) missing.push(t);
    });
    const batches = [];
    for (let i = 0; i < missing.length; i += RLW_CARD_DATA_BATCH) batches.push(missing.slice(i, i + RLW_CARD_DATA_BATCH));

    return Promise.all(batches.map(batch => {
      const url = `${rlwState.rootUrl}search?view=relewise-card-data&type=product&q=${encodeURIComponent(batch.join(' '))}`;
      return fetch(url, { signal, headers: { Accept: 'application/json' } })
        .then(res => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then(body => {
          const json = JSON.parse(body);
          batch.forEach(t => {
            const data = json[t] ?? null;
            result.set(t, data);
            if (_rlwCardDataCache.size >= RLW_CARD_DATA_CACHE_MAX) _rlwCardDataCache.clear();
            _rlwCardDataCache.set(t, data);
          });
        })
        .catch(err => {
          if (err.name === 'AbortError') throw err;
          // A failed batch sends its cards to the Shopify card fetch.
          batch.forEach(t => result.set(t, null));
        });
    })).then(() => result);
  }

  // Card media for the FIRST render, before the background card data lands.
  // Relewise syncs dam.variant_associated_assets only as MediaImage GIDs (no
  // URL), and not the variant's featured image at all — so without this the
  // first render shows the product image and swaps it. One Storefront API call
  // per grid resolves, per card variant: its featured image (Liquid's hero,
  // `current_variant.featured_media`), the product's featured image (hero
  // fallback) and the DAM images (hover). Capped at RLW_CARD_MEDIA_WAIT_MS so a
  // slow call never holds the grid; the background data corrects either way.
  const RLW_CARD_MEDIA_WAIT_MS = 500;
  const _rlwCardMediaCache = new Map();
  const RLW_CARD_MEDIA_QUERY = `
    query RelewiseCardMedia($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          image { url altText width height }
          product { featuredImage { url altText width height } }
          dam: metafield(namespace: "dam", key: "variant_associated_assets") {
            references(first: 6) {
              nodes { ... on MediaImage { image { url altText width height } } }
            }
          }
        }
      }
    }
  `;

  /** File identity of a CDN image URL — host-, size- and query-independent. */
  function rlwImageFileKey(src) {
    if (!src) return '';
    try {
      return new URL(src, window.location.href).pathname.split('/').pop();
    } catch (e) {
      return '';
    }
  }

  // Most pictures a card's touch swipe shows, hero included
  // (_card-swipe-slides.liquid). RLW_CARD_MEDIA_QUERY's references(first: 6)
  // covers it: DAM[0] is normally the hero itself.
  const RLW_CARD_SWIPE_MAX = 5;

  /**
   * Hero and (pinned cards only) hover and swipe slides for one variant, from
   * its Storefront data, with product-card.liquid's rules: hero = the variant's
   * featured image, else the product's; hover = the first DAM image after
   * DAM[0] that is not the hero (compared by file, as the Liquid compares CDN
   * paths); slides = the hover, then every other DAM image that is neither
   * (_card-swipe-slides.liquid). An unpinned card's hover — and so its slides —
   * also depends on every other variant's image, so both are left to the
   * background data. Hover disabled → no hover, the slides still come.
   */
  function rlwCardMediaFrom(node, pinned, ctx) {
    // product-card.liquid:69-76 — the variant's image only when the card is
    // pinned or "Use first variant image as card image" is on.
    const useVariantImage = pinned || ctx.settings.card_use_variant_image !== false;
    const hero = (useVariantImage && rlwCardImageFrom(node.image)) || rlwCardImageFrom(node.product?.featuredImage);
    const media = { hero };
    // A metafield the storefront can't read comes back null: no hover or slide
    // claim either way, so leave both undefined for the background data to settle.
    if (pinned && hero && ctx.settings.card_use_variant_image !== false && node.dam) {
      const heroKey = rlwImageFileKey(hero.src);
      const dam = (node.dam.references?.nodes || []).map(n => n?.image).filter(Boolean);
      const hover = ctx.settings.card_hover_action !== 'none'
        ? dam.slice(1).find(img => rlwImageFileKey(img.url) !== heroKey) || null
        : null;
      if (ctx.settings.card_hover_action !== 'none') media.hover = hover ? rlwCardImageFrom(hover) : null;
      const hoverKey = hover ? rlwImageFileKey(hover.url) : '';
      const rest = dam.filter(img => ![heroKey, hoverKey].includes(rlwImageFileKey(img.url)));
      media.slides = [hover, ...rest].filter(Boolean).slice(0, RLW_CARD_SWIPE_MAX - 1).map(rlwCardImageFrom);
    }
    return media;
  }

  /**
   * Storefront media for the given variant ids, cached for the session.
   * Resolves to Map(variantId → Storefront node); never rejects — a failed or
   * unconfigured call just leaves the first render on the Relewise image.
   */
  function rlwFetchCardMedia(variantIds, signal) {
    const result = new Map();
    const missing = [...new Set(variantIds)].filter(id => {
      if (_rlwCardMediaCache.has(id)) { result.set(id, _rlwCardMediaCache.get(id)); return false; }
      return true;
    });
    const token = window.relewiseAPI.storefront_api_token;
    if (!missing.length || !token || !rlwPrefix) return Promise.resolve(result);
    return fetch(`https://${rlwPrefix}/api/2026-07/graphql.json`, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({
        query: RLW_CARD_MEDIA_QUERY,
        variables: { ids: missing.map(id => `gid://shopify/ProductVariant/${id}`) },
      }),
    })
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        (json?.data?.nodes || []).forEach(node => {
          const id = String(node?.id || '').split('/').pop();
          if (!id) return;
          if (_rlwCardMediaCache.size >= RLW_CARD_DATA_CACHE_MAX) _rlwCardMediaCache.clear();
          _rlwCardMediaCache.set(id, node);
          result.set(id, node);
        });
        return result;
      })
      .catch(() => result);
  }

  // Longest a card waits behind its skeleton for the main image — a stalled
  // image must not hide the card (title, price, link) indefinitely.
  const RLW_IMAGE_REVEAL_CAP_MS = 4000;

  /** The card's main <img>, or null (placeholder SVG / no image). */
  function rlwMainImage(li) {
    return li.querySelector('.card__main-image');
  }

  // `complete` is also true for an image that failed — it fires no further
  // events, and its alt text is what the grid will show either way.
  function rlwImageReady(img) {
    return !img || img.complete;
  }

  /**
   * Keeps a freshly inserted card's IMAGE BOX a skeleton until its main image
   * has loaded (or failed, or RLW_IMAGE_REVEAL_CAP_MS passed); the rest of the
   * card shows at once. See .rlw-media-pending in relewise.css. A below-the-fold
   * card's image box simply shimmers until lazy loading reaches it.
   *
   * hold: the card's image is a stand-in (Relewise's product image, because the
   * Storefront lookup missed its window) — revealing it on load would show the
   * wrong photo before the real one. The box stays a skeleton until the card is
   * replaced (rlwRenderClientCards); the longer cap only covers a replacement
   * that never comes.
   */
  function rlwRevealWhenImageReady(li, hold = false) {
    const img = rlwMainImage(li);
    if (!img || (!hold && rlwImageReady(img))) return;
    li.classList.add('rlw-media-pending');

    let timer;
    const reveal = () => {
      clearTimeout(timer);
      img.removeEventListener('load', reveal);
      img.removeEventListener('error', reveal);
      li.classList.remove('rlw-media-pending');
    };
    if (hold) {
      timer = setTimeout(reveal, RLW_IMAGE_REVEAL_CAP_MS * 2);
      return;
    }
    img.addEventListener('load', reveal);
    img.addEventListener('error', reveal);
    timer = setTimeout(reveal, RLW_IMAGE_REVEAL_CAP_MS);
  }

  /**
   * Resolves once the browser has the image an <img> would pick (same srcset
   * and sizes), fetched off-DOM — used only when a card that is already showing
   * changes image, so the swap happens with the new file ready instead of
   * dropping back to a skeleton. Never rejects; capped like the reveal.
   */
  function rlwPreloadImage(img) {
    if (!img) return Promise.resolve();
    return new Promise(resolve => {
      const pre = new Image();
      const done = () => { clearTimeout(timer); resolve(); };
      const timer = setTimeout(done, RLW_IMAGE_REVEAL_CAP_MS);
      pre.onload = done;
      pre.onerror = done;
      if (img.getAttribute('sizes')) pre.sizes = img.getAttribute('sizes');
      if (img.getAttribute('srcset')) pre.srcset = img.getAttribute('srcset');
      pre.src = img.getAttribute('src');
    });
  }

  /** Renders a model into a fresh grid <li>. */
  function rlwClientCardLi(model, ctx) {
    const li = document.createElement('li');
    li.innerHTML = window.RelewiseCards.html(model, ctx);
    return li;
  }

  /**
   * Swaps a provisional card for its corrected render. A hero/hover <img>
   * showing the same FILE is carried over — the Storefront API and Liquid
   * spell the same image with different hosts and params — so an image the
   * browser already painted is not re-requested and doesn't flash. Its class
   * list is taken from the new render (packshot styling follows the new alt).
   */
  function rlwReplaceClientCard(oldLi, newLi, hold = false) {
    const oldPending = oldLi.classList.contains('rlw-media-pending');
    ['.card__main-image', '.card__hover-image'].forEach(sel => {
      const before = oldLi.querySelector(sel);
      const after = newLi.querySelector(sel);
      if (before && after && rlwImageFileKey(before.getAttribute('src')) === rlwImageFileKey(after.getAttribute('src'))) {
        before.className = after.className;
        after.replaceWith(before);
      }
    });
    const swap = () => {
      if (!oldLi.isConnected) return;
      oldLi.replaceWith(newLi);
      rlwRevealWhenImageReady(newLi, hold);
    };
    // A card whose image box is still a skeleton just swaps (the skeleton
    // carries over); a card already showing an image waits until its NEW image
    // is ready, so its image never drops back to a skeleton.
    if (oldPending || hold || rlwImageReady(rlwMainImage(newLi))) return swap();
    return rlwPreloadImage(rlwMainImage(newLi)).then(swap);
  }

  /**
   * Client-side counterpart of the per-card fetch loop in rlwRenderCards.
   * Renders every card it can at once, then corrects them from one batched
   * background request; declined cards go to the Shopify fetch.
   * @returns {Promise<void>} Settles when every slot holds its final card.
   */
  function rlwRenderClientCards(slots, ctx, signal, seq, fellBack) {
    const pending = [];
    const native = [];

    slots.forEach(({ li, product, handle }) => {
      const model = rlwCardModel(product, ctx);
      if (!model || window.RelewiseCards.unsupported(model).length) {
        native.push(rlwFillNativeSlot(li, product, handle, signal, seq, fellBack));
        return;
      }
      pending.push({ li, product, handle, model, term: rlwCardDataTerm(model) });
    });

    // Both requests leave now, in parallel: the background card data (the
    // correction) and the Storefront media (the first render's images).
    const cardData = rlwFetchCardData(pending.map(x => x.term), signal);
    const media = rlwFetchCardMedia(pending.map(x => x.model.variant.id), signal);
    const mediaOrTimeout = Promise.race([
      media,
      new Promise(resolve => { setTimeout(() => resolve(null), RLW_CARD_MEDIA_WAIT_MS); }),
    ]);

    // Applies a Storefront node's hero/hover to an entry's model; true when it
    // supplied the real hero (the card may then be revealed on image load).
    const applyMedia = (entry, mediaById) => {
      const node = mediaById?.get(entry.model.variant.id);
      if (!node) return false;
      const found = rlwCardMediaFrom(node, Boolean(entry.model.pinnedVariantId), ctx);
      if (found.hover !== undefined) entry.model.media.hover = found.hover;
      if (found.slides !== undefined) entry.model.media.slides = found.slides;
      if (!found.hero) return false;
      entry.model.media.hero = found.hero;
      return true;
    };

    const rendered = mediaOrTimeout.then(mediaById => {
      if (seq !== _rlwFetchSeq) return;
      pending.forEach(entry => {
        entry.authoritative = applyMedia(entry, mediaById);
        // No Relewise price (seen on some chaise-longue variants): the card
        // renders without its price block, which arrives with the shop price
        // in the background data.
        const cardLi = rlwClientCardLi(entry.model, ctx);
        entry.li.replaceWith(cardLi);
        rlwRevealWhenImageReady(cardLi, !entry.authoritative);
        entry.li = cardLi;
      });
    });

    // A Storefront answer that lost the race still beats waiting for the
    // background data: re-render the cards it can fix, which are all still
    // behind their skeletons (held), so this is a swap, never a visible change.
    const lateMedia = Promise.all([media, rendered]).then(([mediaById]) => {
      if (seq !== _rlwFetchSeq) return;
      pending.forEach(entry => {
        if (entry.authoritative || entry.corrected || !applyMedia(entry, mediaById)) return;
        entry.authoritative = true;
        const cardLi = rlwClientCardLi(entry.model, ctx);
        rlwReplaceClientCard(entry.li, cardLi);
        entry.li = cardLi;
      });
    });

    // The correction never waits on the Storefront call: the background data
    // carries the authoritative images itself, and lateMedia skips any card it
    // has already corrected.
    const corrected = Promise.all([cardData, rendered]).then(([dataByTerm]) => {
      if (seq !== _rlwFetchSeq) return null;
      return Promise.all(pending.map(entry => {
        entry.corrected = true;
        const merged = rlwMergeCardData(entry.model, dataByTerm.get(entry.term), ctx);
        if (!merged || window.RelewiseCards.unsupported(merged).length) {
          return rlwFillNativeSlot(entry.li, entry.product, entry.handle, signal, seq, fellBack);
        }
        return rlwReplaceClientCard(entry.li, rlwClientCardLi(merged, ctx));
      }));
    }).catch(err => {
      if (err.name !== 'AbortError') throw err;
    });

    return Promise.allSettled([...native, corrected, lateMedia]).then(() => {});
  }

  function rlwRenderCards(products, grid, seq, wrapper) {
    if (_rlwCardAbort) _rlwCardAbort.abort();
    _rlwCardAbort = new AbortController();
    const signal = _rlwCardAbort.signal;

    grid.innerHTML = '';
    grid.setAttribute('aria-busy', 'true');

    const items = rlwBuildGridItems(products, wrapper);
    // Counted per render pass so a degraded grid reports itself once, with a
    // number — the alternative (silently plainer cards) cost a full review
    // round to identify. Not per card: one line per page load, not sixteen.
    const fellBack = [];
    const clientCtx = rlwClientCardsEnabled() ? rlwCardContext() : null;
    const clientSlots = [];

    const settled = items.map(item => {
      if (item.kind === 'promo') {
        grid.appendChild(item.block.template.content.firstElementChild.cloneNode(true));
        return Promise.resolve();
      }

      const product = item.product;
      const li = rlwSkeletonSlot();
      grid.appendChild(li);

      const handle = product.data?.[`${rlwPrefix}_ShopifyHandle`]?.value || '';
      if (!handle) {
        // Identified by product id, not handle — the missing handle IS the cause
        // here, so it is labelled to keep the warn's list unambiguous.
        fellBack.push(`no-handle:${product.productId}`);
        li.replaceWith(rlwBuildCard(product));
        return Promise.resolve();
      }

      // Client-side cards are rendered together below, so their background
      // data goes out as a few batched requests instead of one per card.
      if (clientCtx) {
        clientSlots.push({ li, product, handle });
        return Promise.resolve();
      }

      return rlwFillNativeSlot(li, product, handle, signal, seq, fellBack);
    });

    if (clientSlots.length) settled.push(rlwRenderClientCards(clientSlots, clientCtx, signal, seq, fellBack));

    Promise.allSettled(settled).then(() => {
      if (seq !== _rlwFetchSeq) return;
      grid.removeAttribute('aria-busy');
      if (fellBack.length) {
        console.warn(
          `[Relewise] ${fellBack.length} of ${products.length} cards fell back to the JS-built card `
          + '(master card fetch missed). Affected:', fellBack
        );
      }
    });
  }

  // ─── Sort ─────────────────────────────────────────────────────────────────

  // Values rlwFetchListing's sorting builder (below) actually knows how to
  // translate into a Relewise sort — 'manual' maps to '' (Relewise has no
  // concept of Shopify's manual collection order, so it falls back to the
  // default in-stock-first relevance sort). 'relevance' (search.sort_options'
  // first entry) maps to '' too, so it is the search page's default option.
  const RLW_DEFAULT_SORT_VALUES = new Set(['manual', 'relevance']);
  const RLW_SUPPORTED_SORT_VALUES = new Set([
    'manual', 'relevance', 'best-selling', 'title-ascending', 'title-descending',
    'price-ascending', 'price-descending', 'created-ascending', 'created-descending',
  ]);

  function rlwRenderSort(wrapper) {
    const container = wrapper.querySelector('.rlw-sort');
    if (!container) return;

    // Sourced from Shopify's own collection.sort_options so labels are
    // translated into every market Shopify supports, matching the native
    // grid's sort dropdown exactly instead of a separately-maintained set.
    // `sort_labels` (relewise-config.liquid) overrides this per-value where a
    // market has bespoke copy — currently only Danish — falling back to
    // Shopify's own translation everywhere else.
    const sortLabels = window.relewiseAPI.sort_labels || {};
    const shopifyOptions = (window.relewiseAPI.sort_options || [])
      .filter(o => RLW_SUPPORTED_SORT_VALUES.has(o.value))
      .map(o => {
        const value = RLW_DEFAULT_SORT_VALUES.has(o.value) ? '' : o.value;
        return { value, label: sortLabels[value] || o.name };
      });

    const options = shopifyOptions.length ? shopifyOptions : [
      { value: '', label: 'Featured' },
      { value: 'best-selling', label: 'Best selling' },
      { value: 'title-ascending', label: 'A–Z' },
      { value: 'title-descending', label: 'Z–A' },
      { value: 'price-ascending', label: 'Price: low–high' },
      { value: 'price-descending', label: 'Price: high–low' },
      { value: 'created-descending', label: 'Newest' },
    ];

    // The design shows the label as visible Semibold text next to the value
    // ("Sortering: Mest populære"), so it becomes a real <label> tied to the
    // select rather than an aria-label — visible text beats a hidden duplicate.
    const sortLabel = window.relewiseAPI.labels?.sort_by || 'Sort by';
    const selectId = 'rlw-sort-select';

    const labelEl = document.createElement('label');
    labelEl.className = 'rlw-sort__label';
    labelEl.setAttribute('for', selectId);
    labelEl.textContent = sortLabel;
    container.appendChild(labelEl);

    const select = document.createElement('select');
    select.className = 'rlw-sort__select';
    select.id = selectId;

    options.forEach(({ value, label }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      if (value === rlwState.currentSorting) opt.selected = true;
      select.appendChild(opt);
    });

    container.appendChild(select);

    select.addEventListener('change', () => {
      rlwState.currentSorting = select.value;
      rlwState.currentPage = 1;
      rlwReload(wrapper);
    });
  }

  // ─── Filter bar ───────────────────────────────────────────────────────────

  function rlwRenderFilterBar(wrapper) {
    const bar = wrapper.querySelector('.rlw-filter-bar');
    if (!bar) return;
    // The row's wrapper owns a toolbar row and its gap, so with no pill it is
    // hidden as a whole rather than left as an empty strip.
    const barWrap = wrapper.querySelector('.rlw-filter-bar-wrap');
    const scrollLeft = bar.scrollLeft;
    bar.innerHTML = '';

    const configs = rlwGetFacetConfigs();
    if (!configs.length) {
      if (barWrap) barWrap.hidden = true;
      return;
    }

    // One pill per enabled facet, in the same order the shop owner configured
    // them in the Relewise "Collection pages" configuration — not whatever
    // order the API happens to return facets.items in.
    const pills = [];

    configs.forEach(cfg => {
      const facetKey = cfg.key;
      const facet = rlwState.lastFacets.find(f => (f.key || f.field) === facetKey);
      if (!facet) return;
      // Relewise can return a facet with zero selectable values (e.g. a
      // Perfion field unpopulated on every product in this result set).
      // Price is exempt: its "available" is a {min, max} object, not a list.
      // It has its own rule instead: nothing found leaves nothing to narrow
      // by price (ClickUp 869fk6gwv), unless the shopper set a price filter —
      // that pill stays so they can widen it back.
      if (cfg.kind === 'price') {
        if (rlwState.lastHits === 0 && !rlwCountActiveFacets(facetKey)) return;
      } else if (cfg.kind === 'range') {
        // Same {value: {lowerBound…, upperBound…}} shape as price. Hidden when
        // no product in the result set carries the field (Relewise answers
        // with a 0..0 range and hits: 0) or every product has the same value —
        // unless it's already engaged, so the shopper can still widen it.
        const range = facet.available?.value;
        const usable = range && facet.available.hits > 0 && range.upperBoundInclusive > range.lowerBoundInclusive;
        if (!usable && !rlwCountActiveFacets(facetKey)) return;
      } else if (!(facet.available?.length || facet.values?.length)) return;
      // Boolean facets only offer "Yes" (rlwBuildBooleanDropdown) — with no
      // `true` value in this result set the dropdown would hold just "Any".
      if (cfg.kind === 'boolean' && !(facet.available || []).some(i => i.value === true || i.value === 'true')) return;

      const activeCount = rlwCountActiveFacets(facetKey);
      const label = cfg.label;

      const pill = document.createElement('button');
      pill.type = 'button';
      // No --active modifier: the design gives an engaged facet no distinct
      // treatment, so the "(n)" in the label below is the only signal it needs.
      pill.className = 'rlw-pill';
      pill.dataset.facetKey = facetKey;
      // No chevron: the design draws facet buttons as plain bordered labels, and
      // an engaged facet reads from the "(n)" plus its chip on the row below.
      // aria-expanded still carries the disclosure state for assistive tech.
      pill.setAttribute('aria-expanded', 'false');
      pill.innerHTML =
        `<span class="rlw-pill__label">${rlwEscape(label)}${activeCount > 0 ? ` (${activeCount})` : ''}</span>`;

      pill.addEventListener('click', e => {
        e.stopPropagation();
        if (_rlwOpenFacetKey === facetKey) {
          _rlwOpenFacetKey = null;
          rlwCloseDropdown();
        } else {
          _rlwOpenFacetKey = facetKey;
          rlwOpenDropdown(pill, facet, cfg, wrapper);
        }
      });

      pills.push(pill);
    });

    // Beyond the configured count, hide the rest behind a trailing "More
    // filters" toggle, appended after the visible pills rather than leading
    // them. Expanded state persists across re-renders (e.g. reloading after
    // a facet change) until toggled again.
    const overflowPills = pills.slice(rlwState.visibleFacetPills);
    if (!rlwState.moreFiltersExpanded) {
      overflowPills.forEach(p => p.classList.add('rlw-pill--hidden'));
    }

    pills.forEach(pill => bar.appendChild(pill));

    if (overflowPills.length) {
      const moreBtn = document.createElement('button');
      moreBtn.type = 'button';
      moreBtn.className = 'rlw-pill rlw-pill--more';
      moreBtn.setAttribute('aria-expanded', String(rlwState.moreFiltersExpanded));
      // Collapsed it offers the hidden count ("Flere filtre (8)"); expanded it
      // offers to hide them again ("Færre filtre"), with no count.
      const labels = window.relewiseAPI.labels || {};
      const moreLabel = rlwState.moreFiltersExpanded
        ? (labels.fewer_filters || 'Fewer filters')
        : (labels.more_filters || 'More filters (__count__)').replace('__count__', overflowPills.length);
      // The ILVA filter glyph from the design (ILVA_Icon_Web_Filter, 19×19) —
      // inlined because this button is built in JS and cannot render a Liquid
      // snippet. Not snippets/icon-filter.liquid: that one is the base theme's
      // stroked sliders icon, a near-miss with the knobs mirrored.
      // Filled paths, so `fill` (not `stroke`) carries the colour, and it takes
      // currentColor rather than the design's literal #322828 so the glyph
      // follows the button's own ink and any hover state.
      moreBtn.innerHTML =
        `<svg class="rlw-pill__icon" viewBox="0 0 19 19" fill="currentColor"`
        + ` aria-hidden="true" focusable="false" role="presentation">`
        + `<path d="M14.5904 6.41644H8.63312V7.16853H14.5904V6.41644Z"/>`
        + `<path d="M6.60648 9.16752H7.42586V4.40961H6.60648V6.41648H4.40961V7.16857H6.60648V9.16752Z"/>`
        + `<path d="M10.3669 11.8433H4.40961V12.5954H10.3669V11.8433Z"/>`
        + `<path d="M11.5742 9.83252V14.5904H12.3935V12.5954H14.5904V11.8434H12.3935V9.83252H11.5742Z"/>`
        + `</svg>`
        + `<span class="rlw-pill__label">${rlwEscape(moreLabel)}</span>`;
      moreBtn.addEventListener('click', () => {
        // The re-render below empties the bar, so this button is destroyed and
        // focus would drop to <body>. Hand it to the new toggle — but only if
        // this one had it: a mouse click in Safari or a tap on iOS never
        // focuses a button, and focusing it here would paint a ring they never
        // asked for.
        const hadFocus = document.activeElement === moreBtn;
        rlwState.moreFiltersExpanded = !rlwState.moreFiltersExpanded;
        rlwRenderFilterBar(wrapper);
        if (!hadFocus) return;
        const next = wrapper.querySelector('.rlw-pill--more');
        if (!next) return;
        // preventScroll keeps the row at the scrollLeft the render restored.
        // Keyboard users still need to see where focus went — on mobile the
        // revealed pills push the toggle far off-screen — so scroll only when
        // the browser shows a focus ring. The toggle is always the row's last
        // item, so scroll the row to its end: scrollIntoView would stop with
        // the toggle flush against the edge, under the scroll-hint arrow.
        next.focus({ preventScroll: true });
        if (next.matches(':focus-visible')) {
          const row = next.closest('.rlw-filter-bar');
          row.scrollLeft = row.scrollWidth;
        }
      });
      bar.appendChild(moreBtn);
    }

    if (barWrap) barWrap.hidden = bar.childElementCount === 0;
    bar.scrollLeft = scrollLeft;
    rlwUpdateScrollHint(wrapper);
  }

  // Mobile's filter row scrolls sideways (see .rlw-filter-bar in
  // relewise.css); the clipped edge of an off-screen pill is normally hint
  // enough, but once "More filters" expands the row further off-screen an
  // explicit arrow earns its place. Shown only while there's content past the
  // visible edge — hidden once scrolled to the end or when everything fits.
  function rlwUpdateScrollHint(wrapper) {
    const bar = wrapper.querySelector('.rlw-filter-bar');
    const hint = wrapper.querySelector('.rlw-scroll-hint');
    if (!bar || !hint) return;
    const hasOverflow = bar.scrollWidth > bar.clientWidth + 1;
    const atEnd = bar.scrollLeft + bar.clientWidth >= bar.scrollWidth - 1;
    hint.classList.toggle('is-visible', hasOverflow && !atEnd);
  }

  // One small removable chip per active facet *value* (not per facet — a
  // string facet with 2 selected values gets 2 chips), plus a "Clear all"
  // link shown only while at least one chip exists.
  function rlwRenderActiveFilters(wrapper) {
    const container = wrapper.querySelector('.rlw-active-filters');
    if (!container) return;
    container.innerHTML = '';

    const chips = [];

    rlwGetFacetConfigs().forEach(cfg => {
      const active = rlwState.activeFacets[cfg.key];
      if (active == null) return;

      if ((cfg.kind === 'string' || cfg.kind === 'number') && Array.isArray(active)) {
        active.forEach(value => {
          chips.push({
            label: String(value),
            onRemove: () => {
              const next = (rlwState.activeFacets[cfg.key] || []).filter(v => v !== value);
              if (next.length) rlwState.activeFacets[cfg.key] = next;
              else delete rlwState.activeFacets[cfg.key];
            },
          });
        });
      } else if (cfg.kind === 'boolean' && typeof active === 'boolean') {
        chips.push({
          label: `${cfg.label}: ${rlwBooleanLabel(active)}`,
          onRemove: () => { delete rlwState.activeFacets[cfg.key]; },
        });
      } else if ((cfg.kind === 'price' || cfg.kind === 'range') && active && (active.min != null || active.max != null)) {
        const parts = [];
        if (active.min != null) parts.push(cfg.format(active.min));
        if (active.max != null) parts.push(cfg.format(active.max));
        chips.push({
          label: `${cfg.label}: ${parts.join(' – ')}`,
          onRemove: () => { delete rlwState.activeFacets[cfg.key]; },
        });
      }
    });

    if (!chips.length) {
      container.hidden = true;
      return;
    }

    container.hidden = false;

    chips.forEach(chip => {
      const chipBtn = document.createElement('button');
      chipBtn.type = 'button';
      chipBtn.className = 'rlw-chip';
      // The design uses the ILVA X glyph here, not a "×" character — the text
      // glyph sits off-centre against a 12px label and can't be sized reliably.
      chipBtn.innerHTML = `
        <span class="rlw-chip__label">${rlwEscape(chip.label)}</span>
        <span class="rlw-chip__remove">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.2"
               aria-hidden="true" focusable="false" role="presentation">
            <path stroke-linecap="round" d="m3.5 3.5 7 7M10.5 3.5l-7 7"/>
          </svg>
        </span>`;
      chipBtn.setAttribute('aria-label', `Remove ${chip.label}`);
      chipBtn.addEventListener('click', () => {
        chip.onRemove();
        rlwState.currentPage = 1;
        _rlwOpenFacetKey = null;
        rlwCloseDropdown();
        rlwReload(wrapper);
      });
      container.appendChild(chipBtn);
    });

    const clearAll = document.createElement('button');
    clearAll.type = 'button';
    // `.rlw-clear-all` owns the type now (Semibold 12px, per the design), so the
    // theme's `link text-sm` utilities would only fight it.
    clearAll.className = 'rlw-clear-all';
    clearAll.textContent = window.relewiseAPI.labels?.clear_all || 'Clear all';
    clearAll.addEventListener('click', () => {
      rlwState.activeFacets = {};
      rlwState.currentPage = 1;
      _rlwOpenFacetKey = null;
      rlwCloseDropdown();
      rlwReload(wrapper);
    });
    container.appendChild(clearAll);
  }

  // ─── Dropdown ─────────────────────────────────────────────────────────────

  function rlwCloseDropdown() {
    document.querySelectorAll('.rlw-dropdown').forEach(el => el.remove());
    document.querySelectorAll('.rlw-pill--open').forEach(el => {
      el.classList.remove('rlw-pill--open');
      el.setAttribute('aria-expanded', 'false');
    });
    _rlwOpenDropdown = null;
  }

  function rlwOpenDropdown(pill, facet, cfg, wrapper) {
    rlwCloseDropdown();

    pill.classList.add('rlw-pill--open');
    pill.setAttribute('aria-expanded', 'true');

    const dropdown = document.createElement('div');
    dropdown.className = 'rlw-dropdown';
    dropdown.setAttribute('role', 'dialog');
    dropdown.setAttribute('aria-label', cfg.label);

    if (cfg.kind === 'price' || cfg.kind === 'range') {
      rlwBuildPriceDropdown(dropdown, facet, cfg, wrapper);
    } else if (cfg.kind === 'boolean') {
      rlwBuildBooleanDropdown(dropdown, facet, cfg, wrapper);
    } else {
      rlwBuildStringDropdown(dropdown, facet, cfg, wrapper);
    }

    const toolbar = wrapper.querySelector('.rlw-toolbar');
    toolbar.appendChild(dropdown);

    _rlwOpenDropdown = { dropdown, pill, toolbar };
    rlwPositionDropdown(dropdown, pill, toolbar);
  }

  // Anchored below its pill, in toolbar-relative coordinates (the dropdown is a
  // child of .rlw-toolbar, not of the scrolling filter row, so the row's
  // overflow can't clip it).
  function rlwPositionDropdown(dropdown, pill, toolbar) {
    const toolbarRect = toolbar.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    dropdown.style.top = (pillRect.bottom - toolbarRect.top + toolbar.scrollTop + 6) + 'px';
    // Clamped into the toolbar: once the filter row scrolls, a pill can sit far
    // enough right that an unclamped panel would hang off the screen. On desktop
    // every pill is well inside the limit, so this is a no-op there.
    const maxLeft = Math.max(0, toolbarRect.width - dropdown.offsetWidth);
    const wanted = pillRect.left - toolbarRect.left;
    dropdown.style.left = Math.min(Math.max(0, wanted), maxLeft) + 'px';
  }

  function rlwBuildStringDropdown(dropdown, facet, cfg, wrapper) {
    const availableValues = (facet.available || facet.values || []);
    const selectedValues = rlwState.activeFacets[cfg.key] || [];

    // Sort: selected first, then by hit count desc
    const sorted = [...availableValues].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return (b.hits || 0) - (a.hits || 0);
    });

    const list = document.createElement('ul');
    list.className = 'rlw-dropdown__list';
    list.setAttribute('role', 'list');

    sorted.forEach(item => {
      const value = item.value;
      const hits = item.hits ?? '';
      const isChecked = selectedValues.includes(value);

      const li = document.createElement('li');
      const id = `rlw-opt-${Math.random().toString(36).slice(2)}`;

      li.innerHTML = `
        <label class="rlw-dropdown__option" for="${id}">
          <input class="rlw-dropdown__checkbox" type="checkbox" id="${id}" value="${rlwEscape(String(value))}" ${isChecked ? 'checked' : ''}>
          <span class="rlw-dropdown__option-label">${rlwEscape(String(value))}</span>
          ${hits !== '' ? `<span class="rlw-dropdown__count">${hits}</span>` : ''}
        </label>`;

      li.querySelector('input').addEventListener('change', e => {
        const current = rlwState.activeFacets[cfg.key] || [];
        if (e.target.checked) {
          rlwState.activeFacets[cfg.key] = [...current, value];
        } else {
          const next = current.filter(v => v !== value);
          if (next.length) {
            rlwState.activeFacets[cfg.key] = next;
          } else {
            delete rlwState.activeFacets[cfg.key];
          }
        }
        rlwState.currentPage = 1;
        _rlwOpenFacetKey = cfg.key;
        rlwTrackFacetChange(cfg, String(value));
        rlwReload(wrapper);
      });

      list.appendChild(li);
    });

    dropdown.appendChild(list);
  }

  function rlwBooleanLabel(value) {
    const labels = window.relewiseAPI.labels || {};
    return value ? (labels.yes_option || 'Yes') : (labels.no_option || 'No');
  }

  function rlwBuildBooleanDropdown(dropdown, facet, cfg, wrapper) {
    const availableValues = facet.available || [];
    const selected = rlwState.activeFacets[cfg.key];

    // "Any" clears the filter; "Yes" narrows to true. "No" is deliberately
    // not offered — merchant decision: boolean filters are Alle / Ja only.
    // Relewise returns booleans as true or "true", so match both. The URL
    // parser (rlwReadUrlState) drops `false` for the same reason.
    const options = [
      { value: null, label: window.relewiseAPI.labels?.any_option || 'Any' },
      ...availableValues
        .filter(item => item.value === true || item.value === 'true')
        .map(item => ({ value: true, label: rlwBooleanLabel(true), hits: item.hits })),
    ];

    const list = document.createElement('ul');
    list.className = 'rlw-dropdown__list';
    list.setAttribute('role', 'list');

    options.forEach(item => {
      const isChecked = item.value === null ? selected == null : selected === item.value;
      const li = document.createElement('li');
      const id = `rlw-opt-${Math.random().toString(36).slice(2)}`;

      li.innerHTML = `
        <label class="rlw-dropdown__option" for="${id}">
          <input class="rlw-dropdown__radio" type="radio" name="rlw-bool-${rlwEscape(cfg.key)}" id="${id}" ${isChecked ? 'checked' : ''}>
          <span class="rlw-dropdown__option-label">${rlwEscape(item.label)}</span>
          ${item.hits != null ? `<span class="rlw-dropdown__count">${item.hits}</span>` : ''}
        </label>`;

      li.querySelector('input').addEventListener('change', () => {
        if (item.value === null) {
          delete rlwState.activeFacets[cfg.key];
        } else {
          rlwState.activeFacets[cfg.key] = item.value;
        }
        rlwState.currentPage = 1;
        _rlwOpenFacetKey = null;
        rlwTrackFacetChange(cfg, item.label);
        rlwCloseDropdown();
        rlwReload(wrapper);
      });

      list.appendChild(li);
    });

    dropdown.appendChild(list);
  }

  // Shared by 'price' (SalesPrice) and 'range' (data double range) facets —
  // both results carry the same available.value bounds; cfg.format renders
  // the values (money vs. e.g. "220 cm").
  function rlwBuildPriceDropdown(dropdown, facet, cfg, wrapper) {
    const active = rlwState.activeFacets[cfg.key] || {};
    const format = cfg.format || rlwMoney;
    const isPrice = cfg.kind === 'price';
    // The SDK's PriceRangeFacetResult nests the bounds as
    // `available.value.{lowerBoundInclusive,upperBoundInclusive}` — there is
    // no `available.min`/`.max` on the wire. `facet.selected` (used only if
    // `available` is absent) is a plain DecimalNullableRange, so it already
    // has the two bound fields directly.
    const available = facet.available?.value || facet.selected || {};

    // Bounds come from the facet's own available range, not a fixed 0..N —
    // the two range inputs need real min/max attributes to drag against.
    const boundsMin = Math.floor(available.lowerBoundInclusive ?? 0);
    const boundsMax = Math.ceil(available.upperBoundInclusive ?? (boundsMin + 1));

    const form = document.createElement('div');
    form.className = 'rlw-dropdown__price';

    const labels = window.relewiseAPI.labels || {};
    const minLabel = rlwEscape(isPrice ? (labels.min_price || 'Min') : `${cfg.label} min`);
    const maxLabel = rlwEscape(isPrice ? (labels.max_price || 'Max') : `${cfg.label} max`);
    const applyLabel = rlwEscape(labels.apply || 'Apply');

    // A previously-applied value can fall outside today's bounds (another
    // facet narrowed the result set since it was set) — clamp rather than
    // let a range input silently snap it back to its own min/max on render.
    let minValue = active.min != null ? Math.min(Math.max(active.min, boundsMin), boundsMax) : boundsMin;
    let maxValue = active.max != null ? Math.min(Math.max(active.max, boundsMin), boundsMax) : boundsMax;
    if (minValue > maxValue) {
      [minValue, maxValue] = [maxValue, minValue];
    }

    const uid = Math.random().toString(36).slice(2);

    form.innerHTML = `
      <div class="rlw-price-slider__values">
        <span class="rlw-price-slider__value rlw-price-slider__value--min"></span>
        <span class="rlw-price-slider__value-sep">–</span>
        <span class="rlw-price-slider__value rlw-price-slider__value--max"></span>
      </div>
      <div class="rlw-price-slider">
        <div class="rlw-price-slider__track"></div>
        <div class="rlw-price-slider__range"></div>
        <label class="visually-hidden" for="rlw-price-min-${uid}">${minLabel}</label>
        <input type="range" class="rlw-price-slider__input rlw-price-slider__input--min" id="rlw-price-min-${uid}"
               min="${boundsMin}" max="${boundsMax}" step="1" value="${minValue}">
        <label class="visually-hidden" for="rlw-price-max-${uid}">${maxLabel}</label>
        <input type="range" class="rlw-price-slider__input rlw-price-slider__input--max" id="rlw-price-max-${uid}"
               min="${boundsMin}" max="${boundsMax}" step="1" value="${maxValue}">
      </div>
      <button class="btn btn--primary rlw-dropdown__price-apply" type="button">${applyLabel}</button>`;

    const minInput = form.querySelector('.rlw-price-slider__input--min');
    const maxInput = form.querySelector('.rlw-price-slider__input--max');
    const minValueEl = form.querySelector('.rlw-price-slider__value--min');
    const maxValueEl = form.querySelector('.rlw-price-slider__value--max');
    const rangeEl = form.querySelector('.rlw-price-slider__range');
    const span = boundsMax - boundsMin || 1;

    // The two range inputs overlap the full track (see relewise.css) so
    // either thumb can be grabbed from anywhere along it; this just keeps
    // them from crossing past each other and repaints the highlighted
    // section and the two value labels as they move.
    function syncSlider(moved) {
      let min = parseFloat(minInput.value);
      let max = parseFloat(maxInput.value);
      if (min > max) {
        if (moved === minInput) {
          max = min;
          maxInput.value = String(max);
        } else {
          min = max;
          minInput.value = String(min);
        }
      }
      minValueEl.textContent = format(min);
      maxValueEl.textContent = format(max);
      rangeEl.style.left = `${((min - boundsMin) / span) * 100}%`;
      rangeEl.style.right = `${100 - ((max - boundsMin) / span) * 100}%`;
    }

    minInput.addEventListener('input', () => syncSlider(minInput));
    maxInput.addEventListener('input', () => syncSlider(maxInput));
    syncSlider(minInput);

    form.querySelector('.rlw-dropdown__price-apply').addEventListener('click', () => {
      const min = parseFloat(minInput.value);
      const max = parseFloat(maxInput.value);
      const isFiltered = min > boundsMin || max < boundsMax;
      if (isFiltered) {
        rlwState.activeFacets[cfg.key] = { min: min > boundsMin ? min : null, max: max < boundsMax ? max : null };
      } else {
        delete rlwState.activeFacets[cfg.key];
      }
      rlwState.currentPage = 1;
      _rlwOpenFacetKey = null;
      rlwTrackFacetChange(cfg, isFiltered ? `${min}-${max}` : '');
      rlwCloseDropdown();
      rlwReload(wrapper);
    });

    dropdown.appendChild(form);
  }

  // ─── Pagination ───────────────────────────────────────────────────────────

  function rlwRenderPagination(hits, perPage, page, wrapper) {
    const container = wrapper.querySelector('.rlw-pagination');
    if (!container) return;

    const totalPages = Math.ceil(hits / perPage);
    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const prevDisabled = page <= 1;
    const nextDisabled = page >= totalPages;
    const labels = window.relewiseAPI.labels || {};
    const navLabel = rlwEscape(labels.pagination_label || 'Pagination');
    const prevLabel = rlwEscape(labels.pagination_previous || 'Previous');
    const nextLabel = rlwEscape(labels.pagination_next || 'Next');
    const positionLabel = (labels.pagination_position || '__page__ / __total__')
      .replace('__page__', page).replace('__total__', totalPages);

    container.innerHTML = `
      <nav class="rlw-pager" aria-label="${navLabel}">
        <button class="btn btn--secondary rlw-pager__btn${prevDisabled ? ' rlw-pager__btn--disabled' : ''}"
                data-rel="prev" ${prevDisabled ? 'disabled' : ''} aria-label="${prevLabel}">
          <svg aria-hidden="true" focusable="false" width="8" height="13" viewBox="0 0 8 13" fill="none">
            <path d="M7 1.5 1.5 7 7 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <span class="rlw-pager__current text-sm">${rlwEscape(positionLabel)}</span>
        <button class="btn btn--secondary rlw-pager__btn${nextDisabled ? ' rlw-pager__btn--disabled' : ''}"
                data-rel="next" ${nextDisabled ? 'disabled' : ''} aria-label="${nextLabel}">
          <svg aria-hidden="true" focusable="false" width="8" height="13" viewBox="0 0 8 13" fill="none">
            <path d="M1 1.5 6.5 7 1 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </nav>`;

    container.querySelector('[data-rel="prev"]').addEventListener('click', () => {
      if (rlwState.currentPage > 1) {
        rlwState.currentPage--;
        rlwReload(wrapper);
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    container.querySelector('[data-rel="next"]').addEventListener('click', () => {
      if (rlwState.currentPage < totalPages) {
        rlwState.currentPage++;
        rlwReload(wrapper);
        wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  // ─── Render results ───────────────────────────────────────────────────────

  function rlwRenderResults(data, wrapper, seq) {
    if (!data?.responses?.[0]) return;

    const response = data.responses[0];
    const products = response.results || [];
    const hits = response.hits || 0;

    // Store facets and hits for filter bar rendering
    rlwState.lastFacets = response.facets?.items || [];
    rlwState.lastHits = hits;

    const countEl = wrapper.querySelector('.rlw-result-count');
    if (countEl) {
      const countLabel = window.relewiseAPI.labels?.product_count || '__count__ products';
      countEl.textContent = countLabel.replace('__count__', hits);
    }

    const grid = wrapper.querySelector('.rlw-product-grid');

    // A restored ?p= past the end (the collection shrank since the link was
    // made): jump to the last real page instead of showing "No products".
    if (products.length === 0 && hits > 0 && rlwState.currentPage > 1) {
      const lastPage = Math.max(1, Math.ceil(hits / rlwState.perPage));
      if (rlwState.currentPage > lastPage) {
        rlwState.currentPage = lastPage;
        rlwReload(wrapper);
        return;
      }
    }

    if (products.length === 0) {
      if (_rlwCardAbort) _rlwCardAbort.abort();
      // The search page's own empty state (heading + advice, merchant-editable)
      // when the term itself found nothing — main-search.liquid's template,
      // ClickUp 869fk6gwv. Collection pages have no template, and a search a
      // filter narrowed to nothing keeps the generic line below.
      const emptyTemplate = !rlwHasActiveFacets() && wrapper.querySelector('template.rlw-empty-template');
      // rlw-empty carries the gutter back: the grid itself is full-bleed for the
      // cards' sake, which would otherwise put this message flush against the
      // screen edge. Wide promotions stay edge to edge deliberately.
      if (emptyTemplate) {
        const li = document.createElement('li');
        li.className = 'col-span-full rlw-empty';
        li.appendChild(emptyTemplate.content.cloneNode(true));
        grid.replaceChildren(li);
      } else {
        const emptyLabel = rlwEscape(window.relewiseAPI.labels?.empty || 'No products found.');
        grid.innerHTML = `<li class="col-span-full rlw-empty"><p>${emptyLabel}</p></li>`;
      }
      grid.removeAttribute('aria-busy');
    } else {
      rlwRenderCards(products, grid, seq, wrapper);
    }

    rlwRenderFilterBar(wrapper);
    rlwRenderActiveFilters(wrapper);
    rlwRenderPagination(hits, rlwState.perPage, rlwState.currentPage, wrapper);

    // Re-open dropdown if one was open before reload
    if (_rlwOpenFacetKey) {
      const pill = wrapper.querySelector(`.rlw-pill[data-facet-key="${CSS.escape(_rlwOpenFacetKey)}"]`);
      const facet = rlwState.lastFacets.find(f => (f.key || f.field) === _rlwOpenFacetKey);
      const cfg = rlwGetFacetConfigs().find(c => c.key === _rlwOpenFacetKey);
      if (pill && facet && cfg) {
        rlwOpenDropdown(pill, facet, cfg, wrapper);
      }
    }
  }

  // ─── Load / reload ────────────────────────────────────────────────────────

  // ─── URL state ────────────────────────────────────────────────────────────
  // Page, sort and facets are mirrored into the query string so Back from a
  // PDP, a reload or a shared link restores the same listing:
  //   ?p=3&sort=price-ascending&f.<key>=A&f.<key>=B&f.price=1000-5000
  // `p` rather than `page`: Shopify's own ?page=N drives the server-rendered
  // crawler list (12 per page) and would disagree with Relewise's page size.
  // `sort` rather than `sort_by` and `f.` rather than `filter.` for the same
  // reason — the server never sees these. replaceState, not pushState: Back
  // leaves the listing instead of stepping through every page/filter change.
  // Unrelated params (utm_*, the search page's `q`) are left untouched.
  const RLW_URL_PAGE = 'p';
  const RLW_URL_SORT = 'sort';
  const RLW_URL_FACET_PREFIX = 'f.';

  function rlwWriteUrlState() {
    let url;
    try { url = new URL(window.location.href); } catch (e) { return; }
    const params = url.searchParams;

    [...params.keys()]
      .filter(k => k === RLW_URL_PAGE || k === RLW_URL_SORT || k.startsWith(RLW_URL_FACET_PREFIX))
      .forEach(k => params.delete(k));

    if (rlwState.currentPage > 1) params.set(RLW_URL_PAGE, String(rlwState.currentPage));
    if (rlwState.currentSorting) params.set(RLW_URL_SORT, rlwState.currentSorting);

    rlwGetFacetConfigs().forEach(cfg => {
      const active = rlwState.activeFacets[cfg.key];
      const name = RLW_URL_FACET_PREFIX + cfg.urlKey;
      if (cfg.kind === 'price' || cfg.kind === 'range') {
        if (active && (active.min != null || active.max != null)) {
          params.set(name, `${active.min ?? ''}-${active.max ?? ''}`);
        }
      } else if (cfg.kind === 'boolean') {
        if (typeof active === 'boolean') params.set(name, String(active));
      } else if (Array.isArray(active)) {
        active.forEach(v => params.append(name, String(v)));
      }
    });

    const next = url.pathname + (params.toString() ? `?${params}` : '') + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (next !== current) {
      try { history.replaceState(history.state, '', next); } catch (e) { /* sandboxed/blocked — state just won't persist */ }
    }
  }

  // Must run after rlwState.searchConfig is set: facets are validated against
  // the configured ones, and anything unknown or malformed is dropped (the
  // first rlwReload then rewrites the URL without it).
  function rlwReadUrlState() {
    const params = new URLSearchParams(window.location.search);

    const page = parseInt(params.get(RLW_URL_PAGE), 10);
    if (page > 1) rlwState.currentPage = page;

    const sort = params.get(RLW_URL_SORT);
    if (sort && !RLW_DEFAULT_SORT_VALUES.has(sort) && RLW_SUPPORTED_SORT_VALUES.has(sort)) rlwState.currentSorting = sort;

    rlwGetFacetConfigs().forEach(cfg => {
      let values = params.getAll(RLW_URL_FACET_PREFIX + cfg.urlKey).filter(v => v !== '');
      if (!values.length && cfg.legacyUrlKey && cfg.legacyUrlKey !== cfg.urlKey) {
        values = params.getAll(RLW_URL_FACET_PREFIX + cfg.legacyUrlKey).filter(v => v !== '');
      }
      if (!values.length) return;
      if (cfg.kind === 'price' || cfg.kind === 'range') {
        const [lo, hi] = values[0].split('-');
        const min = lo !== '' && lo != null && isFinite(lo) ? parseFloat(lo) : null;
        const max = hi !== '' && hi != null && isFinite(hi) ? parseFloat(hi) : null;
        if (min != null || max != null) rlwState.activeFacets[cfg.key] = { min, max };
      } else if (cfg.kind === 'boolean') {
        // Only `true` — "No" isn't an offered option (rlwBuildBooleanDropdown),
        // so an old `=false` link would apply a filter the shopper can't see.
        if (values[0] === 'true') rlwState.activeFacets[cfg.key] = true;
      } else if (cfg.kind === 'number') {
        const nums = [...new Set(values.map(Number).filter(Number.isFinite))];
        if (nums.length) rlwState.activeFacets[cfg.key] = nums;
      } else {
        rlwState.activeFacets[cfg.key] = [...new Set(values)];
      }
    });
  }

  function rlwReload(wrapper) {
    rlwWriteUrlState();
    wrapper.classList.add('is-loading');
    const seq = ++_rlwFetchSeq;

    if (_rlwCardAbort) _rlwCardAbort.abort();
    const grid = wrapper.querySelector('.rlw-product-grid');
    if (grid) grid.removeAttribute('aria-busy');

    rlwFetchListing(
      rlwState.collectionId,
      rlwState.searchTerm,
      rlwState.perPage,
      rlwState.currentPage,
      rlwState.currentSorting,
      rlwState.activeFacets
    )
      .then(data => { if (seq === _rlwFetchSeq) rlwRenderResults(data, wrapper, seq); })
      .catch(err => { if (seq === _rlwFetchSeq) console.error(rlwState.searchTerm ? 'Relewise search error:' : 'Relewise collection error:', err); })
      .finally(() => { if (seq === _rlwFetchSeq) wrapper.classList.remove('is-loading'); });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  async function rlwInitListing(wrapper) {
    _rlwListingWrapper = wrapper;
    rlwState.collectionId = wrapper.dataset.collectionId || null;
    rlwState.collectionHandle = wrapper.dataset.collectionHandle || '';
    rlwState.searchTerm = wrapper.dataset.searchTerm || null;
    // Member price collection switch — see the member filter in rlwFetchListing.
    rlwState.memberVariantsOnly = wrapper.hasAttribute('data-member-variants');
    // routes.root_url has no trailing slash on non-default locales ('/en')
    const rootUrl = wrapper.dataset.rootUrl || '/';
    rlwState.rootUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    rlwState.perPage = parseInt(wrapper.dataset.productsPerPage, 10) || 16;
    rlwState.visibleFacetPills = parseInt(wrapper.dataset.maxFilterOptions, 10) || 7;
    rlwState.currentPage = 1;

    // Stash one server-rendered skeleton <li> before the first render clears
    // the grid — reloads (filters, sort, pagination) clone it as placeholders.
    const skeleton = wrapper.querySelector('.rlw-product-grid > li.rlw-skeleton-card');
    if (skeleton) _rlwSkeletonLi = skeleton.cloneNode(true);

    // The Relewise app embed's own script sets window.ShopifyRelewise
    // independently of relewise.js's load order — wait for it (up to 5s)
    // rather than racing it, since Relewise documents no ready event for it.
    rlwState.searchConfig = await rlwWaitForSearchConfiguration(
      5000, 100,
      rlwState.searchTerm ? ['search', 'collection'] : ['collection']
    );
    // A theme-editor re-render started a newer init while this one waited:
    // this wrapper is gone, and its reload would outrun the live one's.
    if (_rlwListingWrapper !== wrapper) return;

    // Restore page/sort/facets from the URL (Back from a PDP, reload, shared
    // link) — before rlwRenderSort so the select shows the restored sort.
    rlwReadUrlState();

    // Bound once on the bar element itself, which is server-rendered and
    // survives every re-render of its pills.
    const filterBar = wrapper.querySelector('.rlw-filter-bar');
    if (filterBar) {
      filterBar.addEventListener('scroll', () => {
        if (_rlwOpenDropdown) {
          const { dropdown, pill, toolbar } = _rlwOpenDropdown;
          if (dropdown.isConnected && pill.isConnected) {
            rlwPositionDropdown(dropdown, pill, toolbar);
          }
        }
        rlwUpdateScrollHint(wrapper);
      }, { passive: true });
      if (!_rlwResizeBound) {
        _rlwResizeBound = true;
        window.addEventListener('resize', () => {
          if (_rlwListingWrapper) rlwUpdateScrollHint(_rlwListingWrapper);
        }, { passive: true });
      }
    }

    rlwRenderSort(wrapper);
    // A URL sort this store doesn't offer would leave the select showing a
    // different option than the one applied — fall back to the default.
    const sortSelect = wrapper.querySelector('.rlw-sort__select');
    if (sortSelect && sortSelect.value !== rlwState.currentSorting) {
      rlwState.currentSorting = '';
      if ([...sortSelect.options].some(o => o.value === '')) sortSelect.value = '';
    }
    rlwReload(wrapper);
  }

  // ─── Global event handlers ────────────────────────────────────────────────

  document.addEventListener('click', e => {
    if (_rlwOpenFacetKey && !e.target.closest('.rlw-dropdown') && !e.target.closest('.rlw-pill')) {
      _rlwOpenFacetKey = null;
      rlwCloseDropdown();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && _rlwOpenFacetKey) {
      _rlwOpenFacetKey = null;
      rlwCloseDropdown();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (!window.Relewise) {
      console.warn('[Relewise] SDK not loaded');
      return;
    }
    const wrapper = document.querySelector('.rlw-collection-wrapper');
    if (wrapper) rlwInitListing(wrapper);
    rlwInitComplementary();
    rlwInitCartRecommendations();
    rlwInitWishlistEngagementListeners();
  });

  // Theme editor: a block setting change (e.g. "Maximum products to show")
  // re-renders the product section with an empty, hidden row, and nothing
  // above runs again. Refill only the re-rendered section, so no other row
  // is fetched twice. The same goes for a listing (search page, collection
  // grid): any setting change comes back as unfilled skeleton cards. The
  // search page's page/article results are fetched by its own inline script,
  // which may not run again either (main-search.liquid).
  document.addEventListener('shopify:section:load', (e) => {
    if (window.Relewise) {
      rlwInitComplementary(e.target);
      const wrapper = e.target.querySelector('.rlw-collection-wrapper');
      if (wrapper) rlwInitListing(wrapper);
    }
    if (typeof window.rlwLoadSearchContent === 'function') window.rlwLoadSearchContent(e.target);
  });

  // ─── Relewise app blocks: collection-scoped card fetches ─────────────────
  // The Relewise app's Product Category block (used on some collection
  // templates instead of the custom grid above) fetches the native card
  // template through the app's global relewise_buildProductViewUrl(), which
  // always builds a bare /products/<handle> URL. The card then renders
  // without `collection` in scope, its links lose the collection context and
  // the PDP breadcrumb collapses to Home › Product. On collection pages,
  // wrap the builder to prefix the current collection — mirroring
  // rlwNativeCardUrl() above — so product-card emits `| within: collection`
  // URLs again and the PDP breadcrumb reads Home › Collection › Product.
  // (Prev/next nav also keys off the same `collection` object, but this
  // theme never passes show_next_prev, so it stays hidden either way.)
  // Safe by construction: the Product Category block only lists products of
  // the current collection, and product-card.liquid falls back to a bare URL
  // for non-members. Shopify also 301s a non-member collection-scoped URL to
  // the bare product URL, which fetch() follows — so a stray non-member never
  // yields an empty tile. The wrap is a no-op when the app embed is absent or
  // on non-collection pages; if the app ever renames the global, breadcrumbs
  // degrade to the pre-fix behaviour without breaking anything.
  document.addEventListener('DOMContentLoaded', () => {
    const original = window.relewise_buildProductViewUrl;
    if (typeof original !== 'function') return;

    const match = window.location.pathname.match(/\/collections\/([^/]+)/);
    if (!match) return;

    window.relewise_buildProductViewUrl = (product, view, includeVariant = true) =>
      original(product, view, includeVariant)
        .replace('/products/', `/collections/${match[1]}/products/`);
  });

  // ─── Relewise app blocks: guard the app's variantResolution crash ─────────
  // Vendor bug, not ours. With "Custom Product Card" set, the app renders each
  // tile through relewiseUIOptions.templates.product, which decides whether to
  // append &variant= to the card URL via (initialize.js, extension relewise-86,
  // web components 1.33.1):
  //
  //   !variantAwareRendering || ["PartialMatchByTerm", "MatchByTerm"]
  //     .includes(product.variantResolution.source)
  //
  // "Variant aware rendering" is ON in the app embed, so the short circuit
  // never fires and variantResolution is dereferenced unguarded. But those two
  // values are search TERM match types: only a search response carries
  // variantResolution at all. A recommendation product does not, so every tile
  // in a Personal / Popular / Product Category block throws "Cannot read
  // properties of undefined (reading 'source')", renders empty, and the row
  // collapses to 4px — reading as a section that vanished. Latent while the
  // card setting is blank, because the crashing branch is only on the
  // custom-card path.
  //
  // Fix: give the app an empty variantResolution when it is missing, so its own
  // .source lookup reads undefined, .includes() is false, and the URL is built
  // without &variant= — exactly what the vendor intends for a product that was
  // not term-matched. Wrapping templates.product instead of reimplementing it
  // keeps the app's favourite-button tail, :host style and default-tile
  // fallback verbatim, so an app update cannot drift from a copied body.
  // Search is untouched: there variantResolution is present and meaningful, so
  // the guard is a no-op and the vendor's term-match semantics still decide.
  // Remove once Relewise guards that dereference; the wrapper is then inert
  // rather than harmful, but it should not outlive the bug.
  document.addEventListener('DOMContentLoaded', () => {
    const templates = window.relewiseUIOptions?.templates;
    if (typeof templates?.product !== 'function') return;

    const original = templates.product;
    templates.product = function (product, ...rest) {
      if (product && !product.variantResolution) product.variantResolution = {};
      return original.call(this, product, ...rest);
    };
  });

  // ─── Search dropdown ──────────────────────────────────────────────────────

  // ─── Search dropdown media (Storefront API) ─────────────────────────────
  // The dropdown's rows need, per exploded variant, the image the theme would
  // show plus the shop price. They used to come from rlwLookupBatch: one
  // `?view=variant-image` PAGE render per row — ~0.2s when Shopify had it
  // cached, 1-2.5s cold, and the search's ByRelevance variants are often cold.
  // Rows past the 500ms cap painted the feed's full-size product image (at
  // times a placeholder shot) and swapped seconds later.
  //
  // Instead: ONE Storefront API request for all rows — the same mechanism and
  // token the collection grid's first render uses (rlwFetchCardMedia), ~0.2s.
  // Image order is the cart line's (cart-items.liquid, ClickUp 869fc9q6g):
  // the variant's own image, then its first dam.variant_associated_assets
  // image, then the product's featured image. The packshot verdict and padding
  // are the JS twin of _packshot-image-class / _packshot-padding, fed the same
  // settings via relewise-config.liquid (as relewise-cards.js does on PLPs).
  //
  // Its own cache, keyed by variant id: it carries no configurator verdict, so
  // writing into _rlwVariantImageCache would stop a later rlwLookupBatch (cart
  // drawer, complementary block) from ever fetching that flag.
  //
  // The same request carries the badge inputs (ClickUp 869fbuexz) — Mix & Save,
  // the price schedule and the campaign references, with the `ax` fallbacks the
  // variant-image view and the Liquid card read — into _rlwShopBadgeCache, so
  // the dropdown's Mix & spar, member and campaign badges follow Shopify, not
  // the lagging feed. Rows Relewise returned with no variant have no id to ask
  // for: they go to rlwLookupBatch, which resolves the first available variant.
  // Needs Storefront read access on these metafield definitions; a definition
  // without it answers null, which reads as "none" (checked 2026-10-07: ilva.dk
  // agrees with the feed variant for variant; ilva.se has no Mix & Save values
  // to compare).
  const RLW_SEARCH_MEDIA_CACHE_MAX = 300;
  const RLW_SEARCH_IMAGE_WIDTH = 144; // 64px tile at 2x, with headroom.
  const _rlwSearchMediaCache = new Map();
  const RLW_SEARCH_MEDIA_FIELDS = `
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        image { url altText }
        price { amount }
        compareAtPrice { amount }
        product { productType featuredImage { url altText } }
        dam: metafield(namespace: "dam", key: "variant_associated_assets") {
          references(first: 3) {
            nodes { ... on MediaImage { image { url altText } } }
          }
        }
        mixAndSave: metafield(namespace: "ilva", key: "mix_and_save") { value }
        ilvaSchedule: metafield(namespace: "ilva", key: "price_schedule") { value }
        axSchedule: metafield(namespace: "ax", key: "price_schedule") { value }
        ilvaCampaigns: metafield(namespace: "ilva", key: "related_campaigns") {
          references(first: 10) { nodes { ... on Metaobject { id } } }
        }
        axCampaigns: metafield(namespace: "ax", key: "related_campaigns") {
          references(first: 10) { nodes { ... on Metaobject { id } } }
        }
      }
    }
  `;

  /**
   * One Storefront node → the badge inputs rlwShopBadges returns, read the way
   * product.variant-image.liquid reads them: `ilva` first, `ax` when blank. A
   * null field is Shopify's "none", as in the view's answer.
   * @returns {{mixAndSave: object|null, schedule: object|null, campaignIds: string[]}}
   */
  function rlwSearchBadgesFrom(node) {
    const json = field => {
      try {
        const v = field?.value ? JSON.parse(field.value) : null;
        return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
      } catch {
        return null;
      }
    };
    const refs = field => (field?.references?.nodes || [])
      .map(n => String(n?.id || '').split('/').pop())
      .filter(Boolean);
    const ilvaIds = refs(node.ilvaCampaigns);
    return {
      mixAndSave: json(node.mixAndSave),
      schedule: json(node.ilvaSchedule) || json(node.axSchedule),
      campaignIds: ilvaIds.length ? ilvaIds : refs(node.axCampaigns),
    };
  }

  /** `url` at the given width when it is a resizable Shopify CDN URL. */
  function rlwImageAtWidth(url, width) {
    const u = rlwResizableUrl(url);
    if (!u) return url || '';
    u.searchParams.set('width', String(width));
    return String(url).startsWith('//') ? `//${u.host}${u.pathname}${u.search}` : u.href;
  }

  /** _packshot-image-class + _packshot-padding for one image alt / product type. */
  function rlwSearchPackshot(alt, productType) {
    const s = window.relewiseAPI.packshot || {};
    const markers = String(s.alt_suffix || '').split(',').map(x => x.trim()).filter(Boolean);
    const text = String(alt || '');
    if (!markers.length || !text || !markers.some(m => text.endsWith(m))) return { packshot: false, pad: '' };
    let desktop = Number(s.pad_desktop || 0);
    let mobile = Number(s.pad_mobile || 0);
    const type = String(productType || '').trim().toLowerCase();
    for (const rule of String(s.rules || '').split(/\r?\n/)) {
      const parts = rule.trim().split('|');
      if (parts.length >= 3 && parts[0].trim().toLowerCase() === type) {
        desktop = Number(parts[1].trim()) || 0;
        mobile = Number(parts[2].trim()) || 0;
        break;
      }
    }
    const pad = desktop > 0 || mobile > 0 ? `--packshot-pad: ${desktop}%; --packshot-pad-m: ${mobile}%;` : '';
    return { packshot: true, pad };
  }

  /**
   * One Storefront node → `{url, packshot, pad, price, compareAtPrice}` (prices
   * in cents, like the variant-image view), or null without a usable image.
   */
  function rlwSearchMediaFrom(node) {
    const useVariantImage = rlwVariantImageEnabled();
    const dam = (node.dam?.references?.nodes || []).map(n => n?.image).filter(img => img?.url);
    const image = (useVariantImage && (node.image?.url ? node.image : dam[0])) || node.product?.featuredImage || null;
    const toCents = money => {
      const n = parseFloat(money?.amount);
      return Number.isFinite(n) ? Math.round(n * 100) : null;
    };
    const price = toCents(node.price);
    if (!image?.url && price == null) return null;
    return {
      url: image?.url ? rlwImageAtWidth(image.url, RLW_SEARCH_IMAGE_WIDTH) : '',
      ...(image?.url ? rlwSearchPackshot(image.altText, node.product?.productType) : { packshot: false, pad: '' }),
      price,
      compareAtPrice: toCents(node.compareAtPrice) || 0,
    };
  }

  /** Cached dropdown media for a result's exploded variant, or null. */
  function rlwSearchMedia(product) {
    const id = rlwVariantIdOf(product);
    return (id && _rlwSearchMediaCache.get(id)) || null;
  }

  /**
   * Resolve dropdown media for every uncached result in one Storefront call.
   * Never rejects. Without a token, or when the call fails, the affected rows
   * fall back to the per-variant page lookup (rlwLookupBatch) as before.
   * @param {Array} products - Relewise product results.
   * @returns {Promise<void>}
   */
  function rlwFetchSearchMedia(products) {
    const list = Array.isArray(products) ? products : [];
    // No variant id, nothing to ask the Storefront API for: the page lookup
    // resolves the first available variant's image, price and badges.
    const variantless = rlwLookupBatch(list.filter(p => !rlwVariantIdOf(p)));

    // Every badge-cache key (handle|variant) per variant id, to file the
    // node's badge inputs where rlwShopBadges looks for them.
    const keysById = new Map();
    list.forEach(p => {
      const id = rlwVariantIdOf(p);
      const key = rlwVariantImageKey(p);
      if (!id || !key) return;
      if (!keysById.has(id)) keysById.set(id, new Set());
      keysById.get(id).add(key);
    });
    // Ask again for a variant whose badges are gone or empty since:
    // rlwLookupBatch clears the badge cache at its cap and writes null on a
    // failed lookup, and rlwShopBadges reads null as "use the feed". A null
    // media entry — no such variant — never had badges and is not asked again.
    const isCached = id => _rlwSearchMediaCache.has(id)
      && (_rlwSearchMediaCache.get(id) === null
        || [...(keysById.get(id) || [])].every(key => _rlwShopBadgeCache.get(key) != null));
    const missing = [...new Set(list.map(rlwVariantIdOf).filter(id => id && !isCached(id)))];
    if (!missing.length) return variantless;
    const fallback = () => rlwLookupBatch(list.filter(p => missing.includes(rlwVariantIdOf(p))));
    const token = window.relewiseAPI.storefront_api_token;
    if (!token || !rlwPrefix) return Promise.all([variantless, fallback()]).then(() => undefined);

    // Market context, so the price is the one the shopper's market shows —
    // what the Liquid lookup rendered. Validated before it goes into the query.
    const country = typeof Shopify !== 'undefined' && /^[A-Z]{2}$/.test(Shopify.country || '') ? Shopify.country : '';
    const query = `query RelewiseSearchMedia($ids: [ID!]!)${country ? ` @inContext(country: ${country})` : ''} {${RLW_SEARCH_MEDIA_FIELDS}}`;

    return fetch(`https://${rlwPrefix}/api/2026-07/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
      body: JSON.stringify({ query, variables: { ids: missing.map(id => `gid://shopify/ProductVariant/${id}`) } }),
    })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then(json => {
        if (!Array.isArray(json?.data?.nodes)) throw new Error('No nodes');
        // `nodes` answers in `ids` order, with null for a deleted/unpublished
        // variant: cache that miss too, so the row keeps its feed fallbacks
        // without asking again.
        json.data.nodes.forEach((node, i) => {
          if (_rlwSearchMediaCache.size >= RLW_SEARCH_MEDIA_CACHE_MAX) _rlwSearchMediaCache.clear();
          const id = node?.id ? String(node.id).split('/').pop() : missing[i];
          _rlwSearchMediaCache.set(id, node ? rlwSearchMediaFrom(node) : null);
          // A null node (deleted/unpublished) leaves the badges to the feed.
          if (!node) return;
          const badges = rlwSearchBadgesFrom(node);
          (keysById.get(id) || []).forEach(key => {
            // Cleared in step with the lookup caches, as rlwLookupBatch does,
            // so no lookup-cached row is left without its badges. The media
            // cache can stay: isCached re-asks a row whose badges are gone.
            if (_rlwShopBadgeCache.size >= RLW_VARIANT_IMAGE_CACHE_MAX) {
              _rlwVariantImageCache.clear();
              _rlwShopPriceCache.clear();
              _rlwShopBadgeCache.clear();
            }
            _rlwShopBadgeCache.set(key, badges);
          });
        });
      })
      .catch(fallback)
      .then(() => variantless);
  }

  async function rlwSearchDropdown(query) {
    const cfg = window.relewiseAPI || {};
    const settings = {
      language: cfg.language || document.documentElement.lang || 'da',
      currency: (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'DKK',
      displayedAtLocation: 'Search overlay',
      user: await rlwRequireUser(),
    };

    // Bolighus pages are identified by a page metafield (not a Relewise content
    // "Type" field). Unlike Product/Variant metafields (prefixed with the shop
    // domain, e.g. `${rlwPrefix}_ilva.mix_and_save`), Content metafields sync
    // unprefixed — confirmed via My Relewise's entity browser, where a synced
    // page shows the key as plain `custom.bolighusside`. Requires the metafield
    // to be enabled for that sync — see rlwBuildSearchHtml.
    const bolighusDataKey = cfg.search_bolighuse_key || 'custom.bolighusside';

    const productSearchRequest = new Relewise.ProductSearchBuilder(settings)
      .setSelectedProductProperties({
        displayName: true,
        dataKeys: [
          'IsAvailable',
          `${rlwPrefix}_ShopifyHandle`,
          `${rlwPrefix}_ImageUrls`,
          `${rlwPrefix}_perfion.fast_lav_pris`,
          // NYHED. Variant-scoped in Perfion, so the variant list below is the
          // one that matters — this product-level request exists purely so the
          // variant-then-product fallback works if the feed ever adds it, same
          // as fast_lav_pris above. Empty when the merchant cleared the setting,
          // hence the filter. ClickUp 869f3974k.
          rlwNyhedDataKey(),
        ].filter(Boolean),
        pricing: true,
        brand: true,
      })
      .setSelectedVariantProperties({
        dataKeys: [
          `${rlwPrefix}_ShopifyVariantId`,
          // Variant-level image, if the feed ever indexes one — see the "Source 1"
          // comment above rlwVariantImageFromFeed. Free win, no-op today since the
          // Shopify feed only indexes ImageUrls/ImageUrl at product level.
          `${rlwPrefix}_ImageUrls`,
          `${rlwPrefix}_ImageUrl`,
          // FAST LAV PRIS. The Perfion definition is variant-scoped, and the
          // Relewise feed indexes it at variant level only — the product-level
          // key is requested above purely so the theme's variant-then-product
          // fallback still works if the feed ever adds it.
          `${rlwPrefix}_perfion.fast_lav_pris`,
          // Campaign badge inputs. The schedule decides which campaign is
          // active today; related_campaigns is the variant's candidate set.
          // Both are variant-scoped in the feed, matching how
          // product-label.liquid reads them (variant only, no product
          // fallback). The theme also falls back to the `ax.` namespace, but
          // that is not indexed, so there is nothing to request for it.
          `${rlwPrefix}_ilva.price_schedule`,
          `${rlwPrefix}_ilva.related_campaigns`,
          // Mix & Save discount map ({ discounts: { <cfg-id>: <pct> } }) —
          // the configurations themselves ship via relewise-config.liquid
          // (window.relewiseAPI.mix_and_save), since Relewise does not
          // index shop metafields. ClickUp 869dvgvcz.
          `${rlwPrefix}_ilva.mix_and_save`,
          // NYHED — the "New" badge's boolean metafield, variant-scoped in
          // Perfion. Inert until the Relewise feed indexes it, the same
          // enablement perfion.fast_lav_pris already has. Empty when the
          // merchant cleared the setting, hence the filter. ClickUp 869f3974k.
          rlwNyhedDataKey(),
        ].filter(Boolean),
      })
      .setTerm(query)
      .filters(rlwStoreScopeFilter)
      .pagination(p => p.setPageSize(4).setPage(1))
      .build();

    // ExplodedVariants is obsolete; VariantRequestSettings replaces it and lets
    // Relewise pick the Variant most relevant to the search term (ByRelevance)
    // instead of each Product's globally most popular Variant. The Relewise SDK
    // bundled by the App Embed predates ProductSearchBuilder#setVariantRequestSettings
    // (throws "not a function"), so the field is set directly on the built
    // request instead — the search API still understands it.
    productSearchRequest.settings = {
      ...productSearchRequest.settings,
      variantRequestSettings: { maxVariantsPerProduct: 2, sorting: 'ByRelevance' },
    };

    const builder = new Relewise.SearchCollectionBuilder(settings)
      .addRequest(
        new Relewise.SearchTermPredictionBuilder(settings)
          .setTerm(query)
          .take(5)
          .build()
      )
      .addRequest(
        new Relewise.ProductCategorySearchBuilder(settings)
          .setSelectedCategoryProperties({
            displayName: true,
            dataKeys: ['Handle', `${rlwPrefix}_ImageUrl`],
          })
          .setTerm(query)
          .filters(rlwCategoryStoreScopeFilter)
          .pagination(p => p.setPageSize(4).setPage(1))
          .build()
      )
      .addRequest(productSearchRequest)
      .addRequest(
        new Relewise.ContentSearchBuilder(settings)
          .setContentProperties({
            displayName: true,
            dataKeys: ['Handle', 'BlogHandle', 'Type', `${rlwPrefix}_ImageUrl`, bolighusDataKey],
          })
          .setTerm(query)
          .filters(rlwContentStoreScopeFilter)
          .pagination(p => p.setPageSize(10).setPage(1))
          .build()
      );

    const searcher = new Relewise.Searcher(
      window.relewiseAPI.relewise_dataid,
      window.relewiseAPI.relewise_api,
      { serverUrl: window.relewiseAPI.relewise_server_url }
    );

    // Prime the variant images before handing the response back (one Storefront
    // call — rlwFetchSearchMedia). rlwBuildSearchProductRow is synchronous —
    // predictive-search.js calls it straight after awaiting this — so this is
    // the only point at which the rows
    // can paint the variant image without a visible swap, and the wait happens
    // inside an await the dropdown was already doing.
    //
    // The same lookup now carries the shop price (ClickUp 869f5wwgt). When the
    // 500ms cap wins, the rows paint the Relewise feed's price, so the uncapped
    // promise rides along on `data` (non-enumerable) and predictive-search.js
    // re-renders the product rows once it lands — for at most one paint.
    return searcher.batch(builder.build()).then(function(data) {
      const prodResponse = (data?.responses || [])
        .find(r => r['$type']?.includes('ProductSearchResponse'));
      const settled = rlwFetchSearchMedia(prodResponse?.results || []);
      // Tighter cap than the recommendation blocks get: those are still `hidden`
      // while they wait, the dropdown is what the shopper is watching.
      return rlwRaceLookups(settled, 500).then(function(landed) {
        if (!landed && data && typeof data === 'object') {
          Object.defineProperty(data, 'rlwLateLookups', { value: settled });
        }
        return data;
      });
    });
  }

  /**
   * Re-render the dropdown's product rows after late lookups landed, so they
   * swap the feed's price for the shop's (and the product image for the
   * variant's). Skipped while the shopper has a product row selected or
   * focused — rebuilding it would drop that selection.
   * @param {Element} container - The dropdown results element.
   * @param {object} data - The batch response rlwSearchDropdown resolved with.
   */
  function rlwRefreshSearchProductRows(container, data) {
    const list = container?.querySelector('.rlw-ps__products');
    if (!list) return;
    if (list.querySelector('[aria-selected="true"]') || list.contains(document.activeElement)) return;
    const prodResponse = (data?.responses || [])
      .find(r => r['$type']?.includes('ProductSearchResponse'));
    const rows = (prodResponse?.results || []).map(rlwBuildSearchProductRow).join('');
    if (rows) list.innerHTML = rows;
  }

  function rlwBuildSearchProductRow(product) {
    const handle = product.data?.[`${rlwPrefix}_ShopifyHandle`]?.value || '';
    // No handle = no page to open. The whole row is one <a>, so a '#' href made
    // it a dead link that silently reloaded the current page (same defect as the
    // recommendation card, ClickUp 869epzhn7). rlwStoreScopeFilter should mean
    // this never arrives; skip the row rather than render an unusable result.
    if (!handle) return '';

    // Deep-link to the exploded variant, not just the product's default one —
    // otherwise the row's image/price (both variant-specific) land the shopper
    // on a page that may show a different variant selected.
    const variantId = rlwVariantIdOf(product);
    const url = variantId ? `/products/${handle}?variant=${variantId}` : `/products/${handle}`;
    // Primed by rlwSearchDropdown before this runs (rlwFetchSearchMedia), so
    // the row paints the variant image straight away instead of swapping into
    // it. Order: a variant image in the feed (none today), the Storefront
    // media, the page lookup (only when the Storefront call was unavailable),
    // then the feed's product image. Every candidate is asked for at tile size
    // — the feed URL is the full-size original (up to several MB).
    const searchMedia = rlwSearchMedia(product);
    const feedVariantImage = rlwVariantImageEnabled() ? rlwVariantImageFromFeed(product) : '';
    const storefrontImage = !feedVariantImage && searchMedia && searchMedia.url ? searchMedia.url : '';
    const imageUrl = rlwImageAtWidth(
      feedVariantImage
        || storefrontImage
        || rlwVariantImage(product)
        || product.data?.[`${rlwPrefix}_ImageUrls`]?.value?.$values?.[0] || '',
      RLW_SEARCH_IMAGE_WIDTH
    );
    // Packshot spacing — the same flag the mini cards honour (ClickUp
    // 869f20g3c). `.rlw-ps__product-media img.img--packshot` in
    // predictive-search.css out-specifies this row's cover rule; the padding
    // vars ride on the media wrapper exactly as on the mini card.
    const lookupMeta = rlwVariantImageMeta(product);
    const imageMeta = storefrontImage ? searchMedia
      : (lookupMeta ? { ...lookupMeta, url: rlwImageAtWidth(lookupMeta.url, RLW_SEARCH_IMAGE_WIDTH) } : null);
    const isPackshot = !!(imageMeta && imageMeta.packshot && imageMeta.url === imageUrl);
    const mediaStyle = isPackshot && imageMeta.pad ? ` style="${rlwEscape(imageMeta.pad)}"` : '';
    const title = product.displayName || '';
    // The shop's catalogue price for this variant (lookup), not the Relewise
    // feed's, which drifts (ClickUp 869f5wwgt). The feed's figures only while
    // the lookup has not answered. The Storefront media carries it first.
    const shopPrice = searchMedia && searchMedia.price != null
      ? {
          salesPrice: searchMedia.price / 100,
          listPrice: searchMedia.compareAtPrice > 0 ? searchMedia.compareAtPrice / 100 : null,
        }
      : rlwShopPrice(product);
    const salesPrice = shopPrice ? shopPrice.salesPrice : rlwPrice(product.salesPrice);
    const listPrice = shopPrice ? shopPrice.listPrice : rlwPrice(product.listPrice);
    // Zero price = missing catalog data — show neither price nor SPAR badge
    // (task 869eba8mv).
    const zeroPrice = !(salesPrice > 0);
    const isOnSale = !zeroPrice && listPrice != null && salesPrice != null && listPrice > salesPrice;
    const isAvailable = product.data?.IsAvailable?.value !== false;
    const vendor = product.brand?.displayName || product.data?.['Vendor']?.value || '';

    // Badges reuse the theme's card labels (.product-label, main.css — loaded on
    // every page) so search matches the rest of the theme instead of carrying its
    // own pill. Copy and the on/off gates come from relewise-config.liquid; this
    // row is built in JS and can't call `t` or read settings itself.
    //
    // The percent is exact, not an "up to" range: the request selects a concrete
    // Variant per result (setVariantRequestSettings), so listPrice/salesPrice are
    // one variant's prices — the same case product-label.liquid handles in its
    // `variant` branch.
    //
    // Deliberate divergence from the card: the row shows at most ONE pill, where a
    // card can stack three. Two pills wrap onto a second line in a 64px row. The
    // order below is the card's own label order (sold out > member > campaign >
    // sale > fast lav pris), so whichever badge a card would show first is the one
    // search shows.
    const cfg = window.relewiseAPI || {};
    const labels = cfg.labels || {};

    // Which price claim applies is decided by window.resolveScheduleState() — the
    // same resolver main.js runs for product cards and the PDP. It owns the active
    // day (Copenhagen), both spellings of the campaign reference, matching that
    // reference to a campaign, appending savingsPercent, and the rules that a
    // member price or a campaign SUPPRESSES the SPAR badge. Never re-implement any
    // of that here — the two would drift and the card is the source of truth.
    //
    // `campaigns` is the entry field the feed cannot fill (it sends metaobject
    // ids, not labels), so the labels come from relewiseAPI.campaigns, emitted
    // by relewise-config.liquid.
    //
    // The schedule, the campaign references and (below) the Mix & Save map come
    // from Shopify via the lookup once it has answered, the feed's copies only
    // until then (ClickUp 869fbuexz): the feed returns some products with no
    // variant at all, which left a row with no member rule, no campaign and no
    // Mix & spar, and it lags Shopify by however long the backend sync takes.
    const digitsOf = v => String(v == null ? '' : v).replace(/\D/g, '');
    const campaignLookup = Array.isArray(cfg.campaigns) ? cfg.campaigns : [];
    const shopBadges = rlwShopBadges(product);
    // Narrow the global lookup to this variant's own references, exactly as the
    // card does, so search can never surface a badge a card would not.
    const referencedIds = new Set(
      (shopBadges
        ? shopBadges.campaignIds
        : rlwDataList(product.variant?.data, `${rlwPrefix}_ilva.related_campaigns`))
        .map(digitsOf)
        .filter(Boolean)
    );
    let schedule = null;
    if (Array.isArray(cfg.campaigns)) {
      schedule = shopBadges
        ? shopBadges.schedule
        : rlwDataJson(product.variant?.data, `${rlwPrefix}_ilva.price_schedule`);
    }

    // Prices go in as minor units — resolveScheduleState divides by 100.
    const scheduleState = typeof window.resolveScheduleState === 'function' && schedule
      ? window.resolveScheduleState({
          price: Math.round((salesPrice || 0) * 100),
          compareAtPrice: Math.round((listPrice || 0) * 100),
          schedule: schedule.days ? schedule : null,
          campaigns: campaignLookup.filter(c => referencedIds.has(digitsOf(c.id))),
        })
      : null;
    // Without the resolver (main.js failed to load) fall back to the naive
    // compare-at test rather than dropping the sale badge entirely.
    const showSale = scheduleState ? scheduleState.showSaleBadge : isOnSale;
    const showCampaign = !!(scheduleState && scheduleState.showCampaign && scheduleState.campaignText);

    // Special/good price ("Skarp pris") hides the struck was-price, matching
    // the card's card-price--hide-compare rule (ClickUp 869ejw780). Falls back
    // to showing it when the resolver is absent — same degradation as showSale.
    // A member day doesn't need it — the branch below replaces the whole line.
    const hideCompare = !!(scheduleState && scheduleState.specialPrice);
    // A member day replaces the price line instead of decorating it, the way the
    // card does: .card-price--member hides the whole .price block (product.css)
    // and shows the member figure over an "Ikke medlem" one. This row keeps the
    // two side by side, and the pill in its footer already says Medlemspris, so
    // only the non-member figure carries a label. Keyed on the price story, not
    // on the badge setting, so switching the label off still leaves both figures
    // — same as the card (ClickUp 869ey749c).
    const memberActive = !!(scheduleState && scheduleState.memberActive);
    let priceHtml = '';
    if (salesPrice != null && !zeroPrice) {
      if (memberActive) {
        const normalLabel = labels.non_member_price ? `${rlwEscape(labels.non_member_price)} ` : '';
        priceHtml = `<span class="rlw-ps__price">${rlwMoney(scheduleState.memberMajor)}</span>`
          + `<span class="rlw-ps__price-normal">${normalLabel}${rlwMoney(salesPrice)}</span>`;
      } else if (isOnSale && !hideCompare) {
        priceHtml = `<span class="rlw-ps__price rlw-ps__price--sale">${rlwMoney(salesPrice)}</span>`
          + `<span class="rlw-ps__price-was">${rlwMoney(listPrice)}</span>`;
      } else {
        priceHtml = `<span class="rlw-ps__price">${rlwMoney(salesPrice)}</span>`;
      }
    }

    // FAST LAV PRIS — variant value wins, product level is only a fallback, and an
    // explicit variant-level false beats a product-level true (same precedence as
    // product-label.liquid). Relewise returns booleans as either true or "true"
    // depending on how the field was mapped, so accept both.
    const flpRaw = (() => {
      const v = product.variant?.data?.[`${rlwPrefix}_perfion.fast_lav_pris`]?.value;
      if (v !== undefined && v !== null) return v;
      return product.data?.[`${rlwPrefix}_perfion.fast_lav_pris`]?.value;
    })();
    // No price-claim badge next to a hidden zero price (task 869eba8mv) — the same
    // `flp_variant.price > 0` guard the snippet and main.js apply.
    const showFlp = (flpRaw === true || flpRaw === 'true') && !zeroPrice;

    // Mix & Save (ClickUp 869dvgvcz) — same rules as <mix-and-save-label> in
    // main.js: the variant must carry a percentage for a configuration whose
    // inclusive date window (Europe/Copenhagen) contains today, and a
    // compare-at reduction, a zero price or an active member day suppresses
    // the claim. The row shows the FIRST active configuration only — one pill
    // per row is this dropdown's deliberate constraint (see order note above).
    const mnsText = (() => {
      const mns = cfg.mix_and_save;
      if (!mns || zeroPrice || isOnSale) return '';
      if (scheduleState && scheduleState.memberActive) return '';
      const template = mns.labelConfig?.labelTemplate || mns.labelTemplate || '';
      const configs = Array.isArray(mns.configurations) ? mns.configurations : [];
      // Shopify's map once the lookup has answered — null there means none,
      // never "ask the feed" (ClickUp 869fbuexz).
      const mixAndSave = shopBadges
        ? shopBadges.mixAndSave
        : rlwDataJson(product.variant?.data, `${rlwPrefix}_ilva.mix_and_save`);
      const discounts = mixAndSave?.discounts;
      if (!template || !configs.length || !discounts) return '';
      let today = '';
      try {
        today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Copenhagen' }).format(new Date());
      } catch (e) {
        today = new Date().toISOString().slice(0, 10);
      }
      const active = configs.find((c) => {
        const pct = parseFloat(discounts[String(c.id)]);
        if (!Number.isFinite(pct) || pct <= 0) return false;
        const from = String(c.validFrom || '').slice(0, 10);
        const to = String(c.validTo || '').slice(0, 10);
        return (!from || from <= today) && (!to || today <= to);
      });
      if (!active) return '';
      return template
        .replaceAll('<QUANTITY>', String(active.minQuantity))
        .replaceAll('<PERCENTAGE>', String(parseFloat(discounts[String(active.id)])));
    })();

    // "Medlemspris" — the price claim on a member day, ahead of the campaign and
    // SPAR badges the resolver already suppresses there (ClickUp 869ey749c).
    // Gated on the same merchant setting as the Liquid badge, so switching the
    // label off clears it from search too.
    const showMember = !!(scheduleState && scheduleState.showMemberBadge)
      && cfg.show_member_price_label !== false
      && !!labels.member_price;

    let badgeHtml = '';
    const showSoldOut = !isAvailable && cfg.show_sold_out_label !== false && !!labels.sold_out;
    if (showSoldOut) {
      badgeHtml = `<span class="product-label product-label--sold-out">${rlwEscape(labels.sold_out)}</span>`;
    } else if (showMember) {
      badgeHtml = `<span class="product-label product-label--member">${rlwEscape(labels.member_price)}</span>`;
    } else if (showCampaign) {
      // Same markup renderCampaignBadge() (main.js) builds for a card: optional
      // icon <img> before a .product-label__text span, colour via the inline
      // custom property the .product-label--campaign rule reads.
      const campaign = scheduleState.campaign || {};
      const campaignStyle = campaign.color
        ? ` style="--campaign-label-bg-color: ${rlwEscape(campaign.color)};"`
        : '';
      const campaignIcon = campaign.icon
        ? `<img class="product-label__icon" src="${rlwEscape(campaign.icon)}" alt="" width="14" height="14" loading="lazy">`
        : '';
      badgeHtml = `<span class="product-label product-label--campaign"${campaignStyle}>`
        + `${campaignIcon}<span class="product-label__text">${rlwEscape(scheduleState.campaignText)}</span></span>`;
    } else if (showSale && cfg.show_sale_label !== false) {
      // Escape the locale template first, then substitute — the placeholder has
      // no special characters, and rlwMoney can carry markup from the shop's
      // money format (injected raw, same as the price spans above).
      let saleText = '';
      if (cfg.sale_label_type === 'percent') {
        const pct = Math.round((1 - salesPrice / listPrice) * 100);
        saleText = rlwEscape(labels.percent_reduction).replace('__amount__', pct);
      } else if (cfg.sale_label_type === 'money') {
        saleText = rlwEscape(labels.value_reduction).replace('__amount__', rlwMoney(listPrice - salesPrice));
      } else {
        saleText = rlwEscape(labels.sale);
      }
      if (saleText) {
        badgeHtml = `<span class="product-label product-label--sale">${saleText}</span>`;
      }
    } else if (showFlp && labels.fast_low_price) {
      // No merchant on/off setting for this one: the card renders it whenever the
      // metafield is true, so search does the same.
      badgeHtml = `<span class="product-label product-label--fast-low-price">${rlwEscape(labels.fast_low_price)}</span>`;
    } else if (mnsText) {
      badgeHtml = `<span class="product-label product-label--mix-and-save">${rlwEscape(mnsText)}</span>`;
    } else if (rlwShowNyhed(product) && labels.new) {
      // Last, mirroring the Liquid order — the New badge is the last block in
      // product-label.liquid. ClickUp 869f3974k.
      badgeHtml = `<span class="product-label product-label--new">${rlwEscape(labels.new)}</span>`;
    }

    return `<li>
      <a class="rlw-ps__product predictive-search__item js-search-link" href="${rlwEscape(url)}">
        <div class="rlw-ps__product-media"${mediaStyle}>
          ${imageUrl
            ? `<img src="${rlwEscape(imageUrl)}" alt="${rlwEscape(title)}"${isPackshot ? ' class="img--packshot"' : ''} decoding="async" width="72" height="72">`
            : '<div class="rlw-ps__product-placeholder"></div>'}
        </div>
        <div class="rlw-ps__product-info">
          <p class="rlw-ps__product-title">${rlwEscape(title)}</p>
          ${vendor ? `<p class="rlw-ps__product-sub">${rlwEscape(vendor)}</p>` : ''}
          <div class="rlw-ps__product-footer">
            <div class="rlw-ps__product-pricing">${priceHtml}</div>
            ${badgeHtml}
          </div>
        </div>
      </a>
    </li>`;
  }

  function rlwBuildSearchHtml(data, query) {
    const cfg = window.relewiseAPI;
    const responses = data?.responses || [];
    // Section headings/CTAs come translated from relewise-config.liquid (labels.search_*);
    // the Danish literals are only a fallback if that payload is missing.
    const labels = cfg.labels || {};
    const t = (key, fallback) => rlwEscape(labels[key] || fallback);

    const predResponse = responses.find(r => r['$type']?.includes('SearchTermPredictionResponse'));
    const categoryResponse = responses.find(r => r['$type']?.includes('ProductCategorySearchResponse'));
    const prodResponse = responses.find(r => r['$type']?.includes('ProductSearchResponse'));
    const contentResponse = responses.find(r => r['$type']?.includes('ContentSearchResponse'));

    const predictions = predResponse?.predictions || [];
    const collections = categoryResponse?.results || [];
    const products = prodResponse?.results || [];
    const contents = contentResponse?.results || [];

    // Bolighus = pages whose custom.bolighusside metafield is set (that
    // metaobject reference carries the store's opening hours/parking/etc — see
    // sections/store-page.liquid), not a Relewise content "Type" match. Content
    // metafields sync unprefixed (unlike Product/Variant metafields), confirmed
    // via My Relewise's entity browser — the key is plain `custom.bolighusside`.
    const bolighusDataKey = cfg.search_bolighuse_key || 'custom.bolighusside';
    const inspirationType = cfg.search_inspiration_type || 'Article';
    const bolighuse = contents.filter(c => {
      const v = c.data?.[bolighusDataKey]?.value;
      return v !== undefined && v !== null && v !== '';
    });
    // "Inspiration og sider": articles and regular pages, in Relewise's
    // relevance order. Bolighus pages are left out — they already have their
    // own section above. Policies are not indexed by Relewise at all, so they
    // come from shop.policies (relewise-config.liquid) matched on title here.
    const isBolighus = c => bolighuse.includes(c);
    const inspiration = contents.filter(c => {
      const type = c.data?.Type?.value;
      if (type === inspirationType) return true;
      return type === 'Page' && !isBolighus(c);
    });
    const queryWords = query.toLowerCase().split(/\s+/).filter(Boolean);
    const policies = queryWords.length
      ? (cfg.search_policies || []).filter(p => {
          const title = String(p.title || '').toLowerCase();
          return queryWords.every(w => title.includes(w));
        })
      : [];

    let html = '';

    if (predictions.length) {
      html += '<ul class="rlw-ps__suggestions">';
      predictions.forEach(p => {
        const term = typeof p === 'string' ? p : (p.term || '');
        if (!term) return;
        html += `<li><a class="rlw-ps__suggestion predictive-search__item js-search-link" href="/search?q=${encodeURIComponent(term)}">${rlwEscape(term)}</a></li>`;
      });
      html += '</ul>';
    }

    if (collections.length) {
      html += `<div class="rlw-ps__section">
        <p class="rlw-ps__section-heading">${t('search_collections', 'Kategorier')}</p>
        <ul class="rlw-ps__collections">`;
      collections.forEach(c => {
        const handle = c.data?.Handle?.value || '';
        const url = handle ? `/collections/${handle}` : '/search?q=' + encodeURIComponent(query);
        html += `<li><a class="rlw-ps__collection predictive-search__item js-search-link" href="${rlwEscape(url)}">${rlwEscape(c.displayName)}</a></li>`;
      });
      html += `</ul></div>`;
    }

    // Build the rows first and gate the section on what actually rendered, not on
    // the raw response length: rlwBuildSearchProductRow skips a product with no
    // handle, so gating on products.length could leave the heading above an empty
    // list.
    const productRows = products.map(rlwBuildSearchProductRow).join('');
    if (productRows) {
      html += `<div class="rlw-ps__section">
        <p class="rlw-ps__section-heading">${t('search_products', 'Produkter')}</p>
        <ul class="rlw-ps__products">${productRows}</ul></div>`;
    }

    if (bolighuse.length) {
      const bolighusUrl = cfg.search_bolighuse_url || '/pages/bolighuse-og-abningstider';
      html += `<div class="rlw-ps__section">
        <div class="rlw-ps__section-header">
          <p class="rlw-ps__section-heading">${t('search_stores', 'Bolighuse')}</p>
          <a href="${rlwEscape(bolighusUrl)}" class="rlw-ps__view-all-link">${t('search_view_all_stores', 'Vis alle bolighuse')}</a>
        </div>
        <ul class="rlw-ps__locations">`;
      bolighuse.forEach(item => {
        const handle = item.data?.Handle?.value || '';
        const itemUrl = handle ? `/pages/${handle}` : bolighusUrl;
        html += `<li><a class="rlw-ps__location predictive-search__item js-search-link" href="${rlwEscape(itemUrl)}">
          <svg class="rlw-ps__pin" aria-hidden="true" focusable="false" width="12" height="16" viewBox="0 0 12 16" fill="none">
            <path d="M6 0C2.69 0 0 2.69 0 6c0 4.5 6 10 6 10s6-5.5 6-10c0-3.31-2.69-6-6-6zm0 8.25A2.25 2.25 0 1 1 6 3.75a2.25 2.25 0 0 1 0 4.5z" fill="currentColor"/>
          </svg>
          ${rlwEscape(item.displayName)}
        </a></li>`;
      });
      html += `</ul></div>`;
    }

    if (inspiration.length || policies.length) {
      html += `<div class="rlw-ps__section">
        <p class="rlw-ps__section-heading">${t('search_inspiration', 'Inspiration og sider')}</p>
        <ul class="rlw-ps__articles">`;
      inspiration.forEach(item => {
        const handle = item.data?.Handle?.value || '';
        let articleUrl;
        if (item.data?.Type?.value === 'Page') {
          if (!handle) return;
          articleUrl = `/pages/${handle}`;
        } else {
          const blogHandle = item.data?.BlogHandle?.value || 'inspiration';
          articleUrl = handle ? `/blogs/${blogHandle}/${handle}` : '/blogs/inspiration';
        }
        const image = item.data?.[`${rlwPrefix}_ImageUrl`]?.value || '';
        html += `<li><a class="rlw-ps__article predictive-search__item js-search-link" href="${rlwEscape(articleUrl)}">
          ${image ? `<div class="rlw-ps__article-media"><img src="${rlwEscape(image)}" alt="${rlwEscape(item.displayName)}" loading="lazy" width="80" height="56"></div>` : ''}
          <span class="rlw-ps__article-title">${rlwEscape(item.displayName)}</span>
        </a></li>`;
      });
      policies.forEach(p => {
        html += `<li><a class="rlw-ps__article predictive-search__item js-search-link" href="${rlwEscape(p.url)}">
          <span class="rlw-ps__article-title">${rlwEscape(p.title)}</span>
        </a></li>`;
      });
      html += `</ul></div>`;
    }

    html += `<div class="rlw-ps__footer">
      <a href="/search?q=${encodeURIComponent(query)}" class="btn btn--secondary w-full rlw-ps__cta">${t('search_view_all', 'Se alle søgeresultater')}</a>
    </div>`;

    return html;
  }

  window._rlwSearch = {
    dropdown: rlwSearchDropdown,
    buildHtml: rlwBuildSearchHtml,
    refreshProductRows: rlwRefreshSearchProductRows,
  };

  // ─── Complementary products block ─────────────────────────────────────────

  const RLW_ATC_ICON = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon">
    <path d="M10 5.18327C11.6542 5.18327 13 6.5291 13 8.18327H13.7917C13.7917 6.0916 12.0917 4.3916 10 4.3916C7.90833 4.3916 6.20833 6.0916 6.20833 8.18327H6.99999C6.99999 6.5291 8.34583 5.18327 10 5.18327Z" fill="currentColor"/>
    <path d="M15.0875 14.4623H4.9125L3.85 8.18311H3.05L4.24584 15.2539H15.7542L16.95 8.18311H16.15L15.0875 14.4623Z" fill="currentColor"/>
  </svg>`;

  // ─── Wishlist integration (theme-native, assets/wishlist.js) ──────────────
  // Relewise renders these cards client-side, so the wishlist-button Liquid
  // snippet can't be used. Instead we inject the button from its exposed
  // template (window.wishlistData.templates.button). wishlist.js's global click
  // delegation on [data-button-wishlist] and the <wishlist-toggle> wrapper's
  // active-state sync then take over. Uses shared wishlist-helpers.js.
  function rlwWishlistButton(handle) {
    if (!window.wishlistHelpers) return '';
    return window.wishlistHelpers.buildButton(handle, false);
  }

  // Product ids whose Relewise record carried no ShopifyHandle in the current
  // mini-card render. The DEFAULT sink for rlwBuildMiniCard — safe only for a
  // caller that drains it synchronously (build every card, then flush, with no
  // await/microtask hop in between).
  //
  // Both mini-card rows now fetch the theme's own mini card per product — the
  // complementary row since ClickUp 869f58rr2, the cart drawer row since
  // 869f5wwgt — so each flush necessarily sits behind an await, which with a
  // shared array would attribute one row's ids to the other's warning. So each
  // row passes its own local array as the sink and flushes from it; neither
  // path can drain the other's, and this default is left for a synchronous
  // caller.
  const _rlwMiniCardNoHandle = [];

  /**
   * Warn once for any handle-less products in the batch just rendered.
   * A missing handle is a catalog/feed gap, not a theme bug — surfacing it
   * keeps it diagnosable instead of silently degrading (ClickUp 869epzhn7).
   * @param {number} total - Number of products rendered in the batch.
   * @param {Array} sink - Ids collected for THIS batch. Defaults to the shared
   *   module-level array; the complementary row passes its own (see above).
   */
  function rlwFlushMiniCardWarnings(total, sink = _rlwMiniCardNoHandle) {
    if (!sink.length) return;
    console.warn(
      `[Relewise complementary] ${sink.length} of ${total} recommended `
      + 'products have no ShopifyHandle — rendered without link or add-to-cart. '
      + 'Affected product ids:', sink.slice()
    );
    sink.length = 0;
  }

  /**
   * @param {Object} product - Relewise product record.
   * @param {boolean} includeWishlist - Render the wishlist heart.
   * @param {Array} noHandleSink - Where to collect this card's product id when
   *   the record carries no ShopifyHandle. Defaults to the shared array; the
   *   complementary row passes its own so its async flush cannot drain the
   *   cart drawer row's ids (see _rlwMiniCardNoHandle above).
   */
  function rlwBuildMiniCard(product, includeWishlist = true, noHandleSink = _rlwMiniCardNoHandle) {
    const handle = rlwHandleOf(product);
    // No handle = no PDP to point at (ClickUp 869epzhn7). A '#' href resolves to
    // the CURRENT page, so a quick-add button carrying it made the drawer fetch
    // the PDP you are already on and show that product's options instead of the
    // recommended one. Treat it like the zero-price gate below: drop the
    // interactive affordances rather than emit ones that lead somewhere wrong.
    // Collected (not warned per card) so a carousel of 8 logs once — the caller
    // flushes via rlwFlushMiniCardWarnings, mirroring the grid's `fellBack` warn.
    if (!handle) noHandleSink.push(product.productId);
    // Deep-link to the variant Relewise recommended — the one this card's
    // image and price describe — so the title link and the quick-add drawer
    // open it, like the native mini card's links (ClickUp 869f5wwgt).
    const variantId = rlwVariantIdOf(product);
    const url = handle ? `/products/${handle}${variantId ? `?variant=${variantId}` : ''}` : '';
    // Primed by the caller (rlwInitComplementary / rlwInitCartRecommendations)
    // before the build loop; falls back to the catalog's product-level list.
    const imageUrl = rlwVariantImage(product)
      || product.data?.[`${rlwPrefix}_ImageUrls`]?.value?.$values?.[0] || '';
    // Packshot spacing, mirroring product-card-mini.liquid: `.img--packshot`
    // (contain + padding, main.css) on the image and the `--packshot-pad` vars
    // on the media wrapper. The flag exists only on the lookup's own image, so
    // it is honoured only while that is the URL being rendered (ClickUp 869f20g3c).
    const imageMeta = rlwVariantImageMeta(product);
    const isPackshot = !!(imageMeta && imageMeta.packshot && imageMeta.url === imageUrl);
    const mediaStyle = isPackshot && imageMeta.pad ? ` style="${rlwEscape(imageMeta.pad)}"` : '';
    const title = product.displayName || '';
    // Shop price from the same lookup, feed price only on a miss — the cart
    // row once showed a price no variant of the product has (ClickUp 869f5wwgt).
    const shopPrice = rlwShopPrice(product);
    const salesPrice = shopPrice ? shopPrice.salesPrice : rlwPrice(product.salesPrice);
    const listPrice = shopPrice ? shopPrice.listPrice : rlwPrice(product.listPrice);
    // Zero price = missing catalog data — hide the price; the ATC stays a
    // quick-add drawer opener, and the drawer hides its CTAs per variant
    // (task 869eba8mv).
    const zeroPrice = !(salesPrice > 0);
    const isOnSale = !zeroPrice && listPrice != null && salesPrice != null && listPrice > salesPrice;
    const isAvailable = product.data?.IsAvailable?.value !== false;

    const imageHtml = imageUrl
      ? `<img src="${rlwEscape(imageUrl)}" alt="${rlwEscape(title)}" class="img-fit${isPackshot ? ' img--packshot' : ''}" loading="lazy" width="120" height="120">`
      : `<div class="media__placeholder dark"></div>`;

    let wasHtml = isOnSale
      ? `<div class="price__sale-details">
           <span class="price__was">
             <span class="visually-hidden js-label">Regular price</span>
             <span class="js-value">${rlwMoney(listPrice)}</span>
           </span>
         </div>`
      : `<span class="price__was"><span class="visually-hidden js-label"></span><span class="js-value"></span></span>`;

    const priceHtml = salesPrice != null && !zeroPrice ? `
      <div class="price${isOnSale ? ' price--on-sale' : ''}">
        <div class="price__default">
          <span class="price__current">
            <span class="visually-hidden js-label">${isOnSale ? 'Sale price' : 'Regular price'}</span>
            <span class="js-value">${rlwMoney(salesPrice)}</span>
          </span>
          ${wasHtml}
        </div>
      </div>` : '';

    // Configurator products get no purchase control at all, mirroring
    // product-card-mini.liquid — the drawer would add an unconfigured variant
    // straight to cart, skipping the configurator the purchase depends on, and
    // the card's title and image links already reach the PDP where it lives
    // (ClickUp 869f44pbb). `rlwIsConfigurator` stays: it is what suppresses the
    // quick-add here.
    let atcHtml = '';
    if (isAvailable && handle && !rlwIsConfigurator(product)) {
      atcHtml = `
      <button type="button" class="product-card-mini__atc js-quick-add no-js-hidden" aria-haspopup="dialog" data-product-url="${rlwEscape(url)}">
        <span class="visually-hidden">Tilføj til kurv</span>
        ${RLW_ATC_ICON}
      </button>
      <a class="product-card-mini__atc js-hidden" href="${rlwEscape(url)}">
        <span class="visually-hidden">Tilføj til kurv</span>
        ${RLW_ATC_ICON}
      </a>`;
    }

    // PIM replaced produktoverskrift with the title_line_1/2 pair (ClickUp
    // 869enr8y6). Both keys are requested and read newest-first: the Relewise
    // catalog is fed separately from Shopify, so the old key may still be the
    // populated one until that feed catches up.
    const desc = (product.data?.[`${rlwPrefix}_perfion.title_line_2`]?.value
      || product.data?.[`${rlwPrefix}_perfion.produktoverskrift`]?.value
      || '').trim();

    return `<div class="card card--row card--related${includeWishlist ? ' js-wishlist-card' : ''} relative flex">
      <div class="card__media">
        <div class="product-card-mini__media"${mediaStyle}>${imageHtml}</div>
      </div>
      <div class="card__info">
        <div>
          <p class="product-card-mini__title mt-0 mb-0">
            ${handle
              ? `<a href="${rlwEscape(url)}" class="card-link text-current">${rlwEscape(title)}</a>`
              : `<span class="card-link text-current">${rlwEscape(title)}</span>`}
          </p>
          ${desc ? `<p class="product-card-mini__subtitle">${rlwEscape(desc)}</p>` : ''}
        </div>
        ${priceHtml}
        ${atcHtml}
      </div>
      ${includeWishlist ? rlwWishlistButton(handle) : ''}
    </div>`;
  }

  function rlwInitCartRecommendations() {
    document.querySelectorAll('[data-rlw-cart-recommendations]').forEach(async function(wrapper) {
      const productId = wrapper.dataset.productId;
      const variantId = wrapper.dataset.variantId;
      const cartProductIds = new Set((wrapper.dataset.cartProductIds || '').split(',').filter(Boolean));
      const fetchCount = parseInt(wrapper.dataset.productsCount, 10) || 8;
      const showCount = parseInt(wrapper.dataset.showCount, 10) || 4;
      if (!productId) return;

      // Nothing is sent without the app's user (see rlwGetUser).
      const user = await rlwGetUser();
      if (!user) return;

      const settings = {
        language: window.relewiseAPI.language || document.documentElement.lang || 'da',
        currency: (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'DKK',
        displayedAtLocation: 'Cart drawer',
        user,
      };

      const reqBuilder = new Relewise.PurchasedWithProductBuilder(settings)
        .setSelectedProductProperties({
          displayName: true,
          dataKeys: ['IsAvailable', `${rlwPrefix}_ShopifyHandle`, `${rlwPrefix}_ImageUrls`, `${rlwPrefix}_perfion.title_line_2`, `${rlwPrefix}_perfion.produktoverskrift`],
          pricing: true,
        })
        .setSelectedVariantProperties({ dataKeys: [`${rlwPrefix}_ShopifyVariantId`] })
        .product({ productId: String(productId), variantId: variantId || null })
        .filters(rlwStoreScopeFilter)
        .setNumberOfRecommendations(fetchCount)
        .build();

      const batchBuilder = new Relewise.ProductsRecommendationCollectionBuilder()
        .addRequest(reqBuilder);

      const recommender = new Relewise.Recommender(
        window.relewiseAPI.relewise_dataid,
        window.relewiseAPI.relewise_api,
        { serverUrl: window.relewiseAPI.relewise_server_url }
      );

      recommender.batchProductRecommendations(batchBuilder.build())
        .then(async function(data) {
          const all = data?.responses?.[0]?.recommendations || [];
          const products = all
            .filter(p => !cartProductIds.has(String(p.productId)))
            .slice(0, showCount);

          if (!products.length) return;

          // Primed first for the JS fallback card (image + shop price), exactly
          // as the complementary row does.
          await rlwPrimeVariantImages(products);

          // The theme's own mini card, cart view (ClickUp 869f5wwgt): pinned to
          // the variant Relewise recommended, priced by the shop, no wishlist
          // heart (see feat/wishlist-cart-recommendation-cards revert) and no
          // energy mark (869f58rr2). Slots are server-rendered, so the order is
          // kept by index whichever fetch lands first; a missed fetch falls
          // back to the JS card. Local sinks for the same reason as the
          // complementary row: the flush now sits behind the fetches.
          const noHandle = [];
          const fellBack = [];
          const settled = [];
          wrapper.querySelectorAll('li').forEach(function(li, i) {
            if (i >= products.length) {
              li.hidden = true;
              return;
            }
            const product = products[i];
            const handle = rlwHandleOf(product);
            if (!handle) {
              li.innerHTML = rlwBuildMiniCard(product, false, noHandle);
              return;
            }
            settled.push(
              rlwFetchNativeCard(rlwMiniCardUrl(handle, rlwVariantIdOf(product), true))
                .then(function(html) {
                  if (html) {
                    li.innerHTML = html;
                    return;
                  }
                  fellBack.push(handle);
                  li.innerHTML = rlwBuildMiniCard(product, false, noHandle);
                })
                .catch(function() {
                  fellBack.push(handle);
                  li.innerHTML = rlwBuildMiniCard(product, false, noHandle);
                })
            );
          });

          await Promise.allSettled(settled);
          if (fellBack.length) {
            console.warn(
              `[Relewise cart recommendations] ${fellBack.length} of ${products.length} cards fell back `
              + 'to the JS-built card (mini card fetch missed). Affected:', fellBack
            );
          }
          rlwFlushMiniCardWarnings(products.length, noHandle);
          wrapper.removeAttribute('hidden');
        })
        .catch(function(err) {
          console.error('[Relewise cart recommendations]', err);
        });
    });
  }

  function rlwInitComplementary(root) {
    (root || document).querySelectorAll('[data-rlw-complementary]').forEach(async function(list) {
      const productId = list.dataset.productId;
      const variantId = list.dataset.variantId;
      const count = parseInt(list.dataset.productsCount, 10) || 4;
      if (!productId) return;

      const cartProductIds = new Set((list.dataset.cartProductIds || '').split(',').filter(Boolean));

      // Nothing is sent without the app's user (see rlwGetUser).
      const user = await rlwGetUser();
      if (!user) return;

      const settings = {
        language: window.relewiseAPI.language || document.documentElement.lang || 'da',
        currency: (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'DKK',
        displayedAtLocation: 'Product page',
        user,
      };

      const reqBuilder = new Relewise.PurchasedWithProductBuilder(settings)
        .setSelectedProductProperties({
          displayName: true,
          dataKeys: ['IsAvailable', `${rlwPrefix}_ShopifyHandle`, `${rlwPrefix}_ImageUrls`, `${rlwPrefix}_perfion.title_line_2`, `${rlwPrefix}_perfion.produktoverskrift`],
          pricing: true,
        })
        .setSelectedVariantProperties({ dataKeys: [`${rlwPrefix}_ShopifyVariantId`] })
        .product({ productId: String(productId), variantId: variantId || null })
        .filters(rlwStoreScopeFilter)
        .setNumberOfRecommendations(count)
        .build();

      const batchBuilder = new Relewise.ProductsRecommendationCollectionBuilder()
        .addRequest(reqBuilder);

      const recommender = new Relewise.Recommender(
        window.relewiseAPI.relewise_dataid,
        window.relewiseAPI.relewise_api,
        { serverUrl: window.relewiseAPI.relewise_server_url }
      );

      recommender.batchProductRecommendations(batchBuilder.build())
        .then(async function(data) {
          let products = data?.responses?.[0]?.recommendations || [];

          if (cartProductIds.size > 0) {
            products = products.filter(p => !cartProductIds.has(String(p.productId)));
          }

          if (!products.length) return;

          // Still primed up front: any slot whose card fetch misses falls back
          // to the JS-built card, which resolves its image and its shop price
          // through this lookup. The block is still `hidden` at this point, so
          // the wait costs no visible reflow.
          await rlwPrimeVariantImages(products);

          // Cart page complementary drops the wishlist heart; PDP keeps it.
          const includeWishlist = !list.closest('.cart-page-recommendations');
          // Both surfaces fetch the theme's own mini card, pinned to the
          // variant Relewise recommended (ClickUp 869f5wwgt): the shop's price
          // and member price for that variant, and links that open it. The
          // cart page asks for the cart view, which drops the heart and the
          // energy mark — the ticket asks for the mark everywhere EXCEPT search
          // and cart (ClickUp 869f58rr2), and the view is what keeps that split
          // now that both rows share one renderer.

          // This row's OWN no-handle ids, not the shared _rlwMiniCardNoHandle:
          // the flush below now sits behind the card fetches, and draining the
          // shared array there would attribute the cart drawer row's ids to this
          // row's warning (see the contract comment on that array).
          const noHandle = [];
          // Handles whose card fetch missed and fell back to the JS card,
          // mirroring the master grid's `fellBack` warn.
          const fellBack = [];

          const settled = products.map(function(product) {
            // Appended before its content resolves, so the carousel keeps the
            // recommender's order no matter which fetch lands first.
            const li = document.createElement('li');
            li.className = 'slider__item';
            list.appendChild(li);

            const handle = rlwHandleOf(product);
            if (!handle) {
              li.innerHTML = rlwBuildMiniCard(product, includeWishlist, noHandle);
              return Promise.resolve();
            }

            return rlwFetchNativeCard(rlwMiniCardUrl(handle, rlwVariantIdOf(product), !includeWishlist))
              .then(function(html) {
                if (html) {
                  li.innerHTML = html;
                  return;
                }
                fellBack.push(handle);
                li.innerHTML = rlwBuildMiniCard(product, includeWishlist, noHandle);
              })
              .catch(function() {
                fellBack.push(handle);
                li.innerHTML = rlwBuildMiniCard(product, includeWishlist, noHandle);
              });
          });

          // Everything below used to run straight after the synchronous build
          // loop; it has to wait for the fetches now, or the row un-hides empty.
          Promise.allSettled(settled).then(function() {
            if (fellBack.length) {
              console.warn(
                `[Relewise complementary] ${fellBack.length} of ${products.length} cards fell back `
                + 'to the JS-built card (mini card fetch missed). Affected:', fellBack
              );
            }
            rlwFlushMiniCardWarnings(products.length, noHandle);

            const block = list.closest('[hidden]');
            if (block) block.removeAttribute('hidden');

            rlwAttachSliderProgress(list);
          });
        })
        .catch(function(err) {
          console.error('[Relewise complementary]', err);
        });
    });
  }

  // ─── Slider progress bar ──────────────────────────────────────────────────
  // The complementary carousel scrolls horizontally on mobile and stacks into
  // rows on desktop. Append a scroll-driven progress bar (same model as
  // sections/collection-list.liquid) under the slider; it self-hides whenever
  // the track isn't horizontally scrollable, so it only appears in slider mode.
  function rlwAttachSliderProgress(list) {
    const slider = list.closest('.slider');
    if (!slider) return;

    // The .slider sits inside a positioned wrapper (.relative). Append the bar
    // after that wrapper so it spans the full width and clears the cards.
    const anchor = slider.closest('.relative') || slider;
    if (anchor.parentElement?.querySelector(':scope > .rlw-slider-progress')) return;

    const progress = document.createElement('div');
    progress.className = 'rlw-slider-progress';
    progress.setAttribute('aria-hidden', 'true');
    const fill = document.createElement('div');
    fill.className = 'rlw-slider-progress__fill';
    progress.appendChild(fill);
    anchor.insertAdjacentElement('afterend', progress);

    let ticking = false;
    function update() {
      const scrollable = slider.scrollWidth > slider.clientWidth + 1;
      progress.style.display = scrollable ? '' : 'none';
      if (scrollable) {
        fill.style.width = (slider.scrollLeft + slider.clientWidth) / slider.scrollWidth * 100 + '%';
      }
      ticking = false;
    }
    function schedule() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    // Plain `scroll` (rAF-throttled) so the fill tracks the finger in real
    // time — NOT `scrollend`, which only fires once after scrolling stops.
    slider.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    requestAnimationFrame(update);
  }

}
